import { useState, useMemo, useRef, useEffect } from 'react';
import { 
  PlusIcon, 
  MagnifyingGlassIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XMarkIcon,
  ArrowPathIcon,
  ChatBubbleLeftIcon,
  PencilIcon,
  TrashIcon,
  PaperClipIcon,
  ArrowDownTrayIcon,
  DocumentIcon,
  PhotoIcon,
  ArrowUpIcon,
  EyeIcon,
  Squares2X2Icon,
  ListBulletIcon,
  Cog6ToothIcon
} from '@heroicons/react/24/outline';
import type { Task, TaskStatus, TaskPriority, TaskAnexo, Colaborador, Departamento, UserSession, TaskCampoConfig, ItemProgresso } from '../../types';
import { compressImagesIfNeeded } from '../../utils/imageCompression';
import { ehAdminMaster } from '../../utils/adminMaster';

interface TasksProps {
  tasks: Task[];
  colaboradores: Colaborador[];
  departamentos: Departamento[];
  session: UserSession;
  onCreateTask: (task: Omit<Task, 'id' | 'criadaEm' | 'atualizadaEm' | 'historico' | 'nivelEscalonamentoAtual'>, arquivos?: File[]) => Promise<void> | void;
  onUpdateTask: (task: Task) => void;
  onDeleteTask: (taskId: string) => void;
  initialTaskId?: string | null;
  onTaskOpened?: () => void;
}

const Tasks = ({ tasks, colaboradores, departamentos, session, onCreateTask, onUpdateTask, onDeleteTask, initialTaskId, onTaskOpened }: TasksProps) => {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [filter, setFilter] = useState<'todas' | 'minhas' | 'solicitadas'>('minhas');
  const [statusFilter, setStatusFilter] = useState<TaskStatus | 'todas'>('todas');
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState<'kanban' | 'lista'>('lista');
  const [tarefaArrastada, setTarefaArrastada] = useState<Task | null>(null);
  const [colunaAlvo, setColunaAlvo] = useState<TaskStatus | null>(null);

  // Sincronizar selectedTask quando tasks mudar (para refletir atualizações como novo prazo)
  useEffect(() => {
    if (selectedTask) {
      const updatedTask = tasks.find(t => t.id === selectedTask.id);
      if (updatedTask && JSON.stringify(updatedTask) !== JSON.stringify(selectedTask)) {
        setSelectedTask(updatedTask);
      }
    }
  }, [tasks, selectedTask]);

  // Abrir tarefa automaticamente via deep link (/t/:taskId)
  useEffect(() => {
    if (initialTaskId && tasks.length > 0) {
      const task = tasks.find(t => t.id === initialTaskId);
      if (task && !canAccessTask(task)) {
        console.warn('🚫 Deep link para tarefa sem acesso:', initialTaskId);
        alert('Você não tem acesso a esta tarefa.');
      } else if (task) {
        console.log('📋 Abrindo tarefa via deep link:', task.titulo);
        setSelectedTask(task);
        setFilter('todas'); // Mostrar todas para garantir que a tarefa seja visível
      }
      onTaskOpened?.();
    }
  }, [initialTaskId, tasks]);

  // Obter colaborador atual
  const currentColaborador = useMemo(() => {
    return colaboradores.find(c => 
      c.email?.toLowerCase() === session.usuario.email?.toLowerCase() || 
      c.id === session.usuario.id ||
      c.userId === session.usuario.id ||
      (c as any).usuarioId === session.usuario.id
    );
  }, [colaboradores, session]);

  const isAdmin = session.usuario.role === 'administrator';
  // 👑 Só o admin master enxerga a agenda inteira da empresa. Administradores comuns
  // seguem a mesma regra de todo mundo: só veem tarefas em que estão envolvidos.
  const isAdminMaster = ehAdminMaster(session.usuario.email);
  const userIds = [
    session.usuario.id,
    session.usuario.email,
    currentColaborador?.id,
    currentColaborador?.userId,
    currentColaborador?.email
  ].filter(Boolean) as string[];

  const matchesUser = (targetId?: string) =>
    !!targetId && userIds.some(id => String(id) === String(targetId));

  const findColaborador = (id?: string) =>
    id
      ? colaboradores.find(c =>
          c.id === id ||
          c.userId === id ||
          (c as any).usuarioId === id ||
          c.email?.toLowerCase() === String(id).toLowerCase()
        )
      : undefined;

  const isInvolvedInTask = (task: Task) => {
    const solicitante = findColaborador(task.solicitanteId);
    const responsavel = findColaborador(task.responsavelId);
    return (
      matchesUser(task.solicitanteId) ||
      matchesUser(task.responsavelId) ||
      matchesUser(solicitante?.userId) ||
      matchesUser((solicitante as any)?.usuarioId) ||
      matchesUser(solicitante?.email) ||
      (solicitante?.nome && solicitante.nome === session.usuario.nome) ||
      matchesUser(responsavel?.userId) ||
      matchesUser((responsavel as any)?.usuarioId) ||
      matchesUser(responsavel?.email) ||
      (responsavel?.nome && responsavel.nome === session.usuario.nome) ||
      (task.observadoresIds || []).some(id => matchesUser(id))
    );
  };

  const isSolicitanteTask = (task: Task) => {
    const solicitante = findColaborador(task.solicitanteId);
    return (
      matchesUser(task.solicitanteId) ||
      matchesUser(solicitante?.userId) ||
      matchesUser((solicitante as any)?.usuarioId) ||
      matchesUser(solicitante?.email) ||
      (solicitante?.nome && solicitante.nome === session.usuario.nome)
    );
  };

  // Superior direto do responsavel continua enxergando a tarefa da propria equipe
  // (ele recebe e-mail na criacao/escalonamento e pode justificar e redefinir prazo).
  const isLiderDoResponsavelTask = (task: Task) => {
    const responsavel = findColaborador(task.responsavelId);
    if (!responsavel?.superiorId || !currentColaborador) return false;
    return responsavel.superiorId === currentColaborador.id ||
           responsavel.superiorId === currentColaborador.userId ||
           responsavel.superiorId === (currentColaborador as any)?.usuarioId;
  };

  // 🔒 Visibilidade da tarefa: envolvido (solicitante, responsavel ou mencionado),
  // lider do responsavel, ou admin master. Ser administrador nao basta.
  const canAccessTask = (task: Task) =>
    isAdminMaster || isInvolvedInTask(task) || isLiderDoResponsavelTask(task);

  // Verificar se pode concluir/alterar status: responsável, solicitante, líder do responsável ou admin
  const canCompleteTask = (task: Task) => {
    if (isAdmin) return true;
    
    const responsavel = getColaboradorById(task.responsavelId);
    const solicitante = getColaboradorById(task.solicitanteId);
    
    // É o responsável
    const isResponsavel = matchesUser(task.responsavelId) ||
      matchesUser(responsavel?.userId) ||
      matchesUser((responsavel as any)?.usuarioId) ||
      matchesUser(responsavel?.email) ||
      (responsavel?.nome && responsavel.nome === session.usuario.nome);
    
    // É o solicitante
    const isSolicitante = matchesUser(task.solicitanteId) ||
      matchesUser(solicitante?.userId) ||
      matchesUser((solicitante as any)?.usuarioId) ||
      matchesUser(solicitante?.email) ||
      (solicitante?.nome && solicitante.nome === session.usuario.nome);
    
    // É o líder do responsável
    const isLiderDoResponsavel = (() => {
      if (!responsavel?.superiorId || !currentColaborador) return false;
      return responsavel.superiorId === currentColaborador.id ||
             responsavel.superiorId === currentColaborador.userId ||
             responsavel.superiorId === (currentColaborador as any)?.usuarioId;
    })();
    
    return isResponsavel || isSolicitante || isLiderDoResponsavel;
  };

  // Arrastar o card do kanban para outra coluna = mudar o status.
  // Mesma permissão de alterar status pelo detalhe da tarefa.
  const soltarNaColuna = (novoStatus: TaskStatus) => {
    const task = tarefaArrastada;
    setTarefaArrastada(null);
    setColunaAlvo(null);
    if (!task || task.status === novoStatus) return;
    if (!canCompleteTask(task)) {
      alert('Você não tem permissão para alterar o status desta tarefa.');
      return;
    }
    onUpdateTask({
      ...task,
      status: novoStatus,
      ...(novoStatus === 'concluida' ? { concluidaEm: new Date() } : {}),
      atualizadaEm: new Date()
    });
  };

  // Filtrar tarefas
  const filteredTasks = useMemo(() => {
    // 🔒 Primeiro corta o que a pessoa nem pode enxergar (o backend ja filtra,
    // isto e a segunda barreira / cobre a lista carregada em memoria).
    let result = tasks.filter(canAccessTask);

    // Filtro por tipo (minhas, solicitadas, todas)
    if (filter === 'minhas' && currentColaborador) {
      result = result.filter(t => t.responsavelId === currentColaborador.id);
    } else if (filter === 'solicitadas' && currentColaborador) {
      result = result.filter(t => t.solicitanteId === currentColaborador.id);
    }

    // Filtro por status (apenas no modo lista)
    if (viewMode === 'lista' && statusFilter !== 'todas') {
      result = result.filter(t => t.status === statusFilter);
    }

    // Filtro por busca
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(t => 
        t.titulo.toLowerCase().includes(term) ||
        t.descricao?.toLowerCase().includes(term)
      );
    }

    // Ordenar por prazo (mais urgente primeiro)
    result.sort((a, b) => new Date(a.prazo).getTime() - new Date(b.prazo).getTime());

    return result;
  }, [tasks, filter, statusFilter, searchTerm, currentColaborador, colaboradores, viewMode]);

  // Agrupar tarefas por status para Kanban
  const tasksByStatus = useMemo(() => {
    const groups: Record<TaskStatus, Task[]> = {
      pendente: [],
      em_andamento: [],
      aguardando: [],
      concluida: [],
      cancelada: []
    };
    
    filteredTasks.forEach(task => {
      if (groups[task.status]) {
        groups[task.status].push(task);
      }
    });
    
    return groups;
  }, [filteredTasks]);

  // Estatísticas
  const stats = useMemo(() => {
    const myTasks = currentColaborador 
      ? tasks.filter(t => t.responsavelId === currentColaborador.id)
      : [];
    
    const now = new Date();
    return {
      total: myTasks.length,
      pendentes: myTasks.filter(t => t.status === 'pendente').length,
      emAndamento: myTasks.filter(t => t.status === 'em_andamento').length,
      atrasadas: myTasks.filter(t => 
        (t.status === 'pendente' || t.status === 'em_andamento') && 
        new Date(t.prazo) < now
      ).length,
      concluidas: myTasks.filter(t => t.status === 'concluida').length
    };
  }, [tasks, currentColaborador]);

  // Helpers
  const getColaboradorById = (id: string) =>
    colaboradores.find(c =>
      c.id === id ||
      c.userId === id ||
      (c as any).usuarioId === id ||
      c.email?.toLowerCase() === String(id).toLowerCase()
    );

  const getStatusConfig = (status: TaskStatus) => {
    const configs = {
      pendente: { label: 'Pendente', color: 'bg-slate-100 text-slate-700', headerColor: 'bg-gradient-to-r from-slate-100 to-slate-50 border-slate-200', icon: ClockIcon, iconColor: 'text-slate-500', countBg: 'bg-slate-200 text-slate-700' },
      em_andamento: { label: 'Em Andamento', color: 'bg-amber-100 text-amber-700', headerColor: 'bg-gradient-to-r from-amber-100 to-amber-50 border-amber-200', icon: ArrowPathIcon, iconColor: 'text-amber-600', countBg: 'bg-amber-200 text-amber-700' },
      aguardando: { label: 'Aguardando', color: 'bg-purple-100 text-purple-700', headerColor: 'bg-gradient-to-r from-purple-100 to-purple-50 border-purple-200', icon: ClockIcon, iconColor: 'text-purple-500', countBg: 'bg-purple-200 text-purple-700' },
      concluida: { label: 'Concluída', color: 'bg-emerald-100 text-emerald-700', headerColor: 'bg-gradient-to-r from-emerald-100 to-emerald-50 border-emerald-200', icon: CheckCircleIcon, iconColor: 'text-emerald-500', countBg: 'bg-emerald-200 text-emerald-700' },
      cancelada: { label: 'Cancelada', color: 'bg-red-100 text-red-700', headerColor: 'bg-gradient-to-r from-red-100 to-red-50 border-red-200', icon: XMarkIcon, iconColor: 'text-red-500', countBg: 'bg-red-200 text-red-700' }
    };
    return configs[status];
  };

  const getPrioridadeConfig = (prioridade: TaskPriority) => {
    const configs = {
      baixa: { label: 'Baixa', color: 'text-gray-500', dot: 'bg-gray-400' },
      normal: { label: 'Normal', color: 'text-gray-600', dot: 'bg-gray-500' },
      alta: { label: 'Alta', color: 'text-amber-600', dot: 'bg-amber-500' },
      urgente: { label: 'Urgente', color: 'text-red-600', dot: 'bg-red-500' }
    };
    return configs[prioridade];
  };

  const isAtrasada = (task: Task) => {
    return (task.status === 'pendente' || task.status === 'em_andamento') && 
           new Date(task.prazo) < new Date();
  };

  const formatDate = (date: Date | string) => {
    return new Date(date).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatDateRelative = (date: Date | string) => {
    const d = new Date(date);
    const now = new Date();
    const diff = d.getTime() - now.getTime();
    const days = Math.ceil(diff / (1000 * 60 * 60 * 24));

    if (days < 0) return `${Math.abs(days)} dias atrasado`;
    if (days === 0) return 'Vence hoje';
    if (days === 1) return 'Vence amanhã';
    if (days <= 7) return `Vence em ${days} dias`;
    return formatDate(date);
  };

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-full mx-auto px-3 sm:px-6 py-4 sm:py-5">
        
        {/* Header - Estilo similar aos Departamentos */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4 sm:mb-6">
          <div className="flex items-center gap-3 sm:gap-4">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gray-100 rounded-xl flex items-center justify-center">
              <CheckCircleIcon className="h-5 w-5 sm:h-6 sm:w-6 text-gray-600" />
            </div>
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Tarefas</h1>
              <p className="text-xs sm:text-sm text-gray-500">{filteredTasks.length} tarefa(s)</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
            {/* Search */}
            <div className="relative flex-1 sm:flex-none">
              <MagnifyingGlassIcon className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar tarefas..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full sm:w-64 pl-9 pr-3 py-2 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-200 focus:border-gray-300"
              />
            </div>
            
            {/* View Toggle */}
            <div className="hidden sm:flex items-center border border-gray-200 rounded-lg overflow-hidden">
              <button
                onClick={() => setViewMode('kanban')}
                className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium transition-colors ${
                  viewMode === 'kanban' 
                    ? 'bg-gray-800 text-white' 
                    : 'bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >
                <Squares2X2Icon className="h-4 w-4" />
                Kanban
              </button>
              <button
                onClick={() => setViewMode('lista')}
                className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium transition-colors ${
                  viewMode === 'lista' 
                    ? 'bg-gray-800 text-white' 
                    : 'bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >
                <ListBulletIcon className="h-4 w-4" />
                Lista
              </button>
            </div>
            
            {/* New Task Button */}
            <button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center gap-2 bg-gray-800 text-white px-3 sm:px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-900 transition-colors"
            >
              <PlusIcon className="h-4 w-4" />
              <span className="hidden sm:inline">Nova Tarefa</span>
              <span className="sm:hidden">Nova</span>
            </button>
          </div>
        </div>

        {/* Filters Bar */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 mb-4 sm:mb-6">
          {/* Filter Tabs */}
          <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1 overflow-x-auto">
            {[
              { key: 'minhas', label: 'Minhas Tarefas', labelShort: 'Minhas' },
              { key: 'solicitadas', label: 'Solicitadas por mim', labelShort: 'Solicitadas' },
              { key: 'todas', label: 'Todas', labelShort: 'Todas' }
            ].map(item => (
              <button
                key={item.key}
                onClick={() => setFilter(item.key as typeof filter)}
                className={`px-3 sm:px-4 py-2 rounded-md text-xs sm:text-sm font-medium transition-all whitespace-nowrap ${
                  filter === item.key 
                    ? 'bg-white text-gray-900 shadow-sm' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <span className="hidden sm:inline">{item.label}</span>
                <span className="sm:hidden">{item.labelShort}</span>
              </button>
            ))}
          </div>

          {/* Status Filter (apenas no modo lista) */}
          {viewMode === 'lista' && (
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as typeof statusFilter)}
              className="px-3 py-2 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-200"
            >
              <option value="todas">Todos os status</option>
              <option value="pendente">Pendente</option>
              <option value="em_andamento">Em Andamento</option>
              <option value="aguardando">Aguardando</option>
              <option value="concluida">Concluída</option>
              <option value="cancelada">Cancelada</option>
            </select>
          )}
          
          {/* Stats Mini */}
          <div className="flex items-center gap-3 sm:gap-4 sm:ml-auto text-xs sm:text-sm">
            <span className="text-gray-500">
              <span className="font-semibold text-gray-900">{stats.pendentes}</span> pendentes
            </span>
            <span className="text-gray-500">
              <span className="font-semibold text-gray-700">{stats.emAndamento}</span> em andamento
            </span>
            {stats.atrasadas > 0 && (
              <span className="text-red-600 font-medium">
                {stats.atrasadas} atrasada{stats.atrasadas > 1 ? 's' : ''}
              </span>
            )}
          </div>
        </div>

        {/* Kanban View */}
        {viewMode === 'kanban' && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5 w-full pb-4">
            {(['pendente', 'em_andamento', 'aguardando', 'concluida'] as TaskStatus[]).map(status => {
              const statusConfig = getStatusConfig(status);
              const tasksInColumn = tasksByStatus[status];
              const StatusIcon = statusConfig.icon;
              
              return (
                <div key={status} className="w-full min-w-0">
                  {/* Column Header */}
                  <div className={`flex items-center justify-between px-4 py-3.5 rounded-t-2xl border-2 shadow-sm ${statusConfig.headerColor}`}>
                    <div className="flex items-center gap-2.5">
                      <div className={`w-8 h-8 rounded-xl flex items-center justify-center bg-white/80 shadow-sm`}>
                        <StatusIcon className={`h-4 w-4 ${statusConfig.iconColor}`} />
                      </div>
                      <span className="font-bold text-gray-800">{statusConfig.label}</span>
                    </div>
                    <span className={`text-xs px-2.5 py-1 rounded-full font-bold ${statusConfig.countBg}`}>
                      {tasksInColumn.length}
                    </span>
                  </div>
                  
                  {/* Column Content — drop zone do drag & drop */}
                  <div
                    onDragOver={(e) => {
                      if (!tarefaArrastada) return;
                      e.preventDefault();
                      e.dataTransfer.dropEffect = 'move';
                      if (colunaAlvo !== status) setColunaAlvo(status);
                    }}
                    onDragLeave={(e) => {
                      if (e.currentTarget.contains(e.relatedTarget as Node)) return;
                      setColunaAlvo(prev => (prev === status ? null : prev));
                    }}
                    onDrop={(e) => {
                      e.preventDefault();
                      soltarNaColuna(status);
                    }}
                    className={`border-x-2 border-b-2 rounded-b-2xl p-3 min-h-[400px] space-y-3 transition-colors ${
                      colunaAlvo === status && tarefaArrastada && tarefaArrastada.status !== status
                        ? 'bg-indigo-50 border-indigo-300 border-dashed'
                        : 'bg-gradient-to-b from-gray-50 to-white border-gray-100'
                    }`}
                  >
                    {tasksInColumn.length > 0 ? (
                      tasksInColumn.map(task => (
                        <TaskKanbanCard 
                          key={task.id} 
                          task={task} 
                          getColaboradorById={getColaboradorById}
                          getPrioridadeConfig={getPrioridadeConfig}
                          isAtrasada={isAtrasada}
                          formatDateRelative={formatDateRelative}
                          showLeaderNotice={Boolean(
                            isAtrasada(task) && // Só mostra se está atrasada
                            !!task.justificativaAtraso &&
                            isSolicitanteTask(task) &&
                            !['concluida', 'cancelada'].includes(task.status)
                          )}
                          canComplete={canCompleteTask(task)}
                          podeArrastar={canCompleteTask(task)}
                          arrastando={tarefaArrastada?.id === task.id}
                          onDragStart={() => setTarefaArrastada(task)}
                          onDragEnd={() => { setTarefaArrastada(null); setColunaAlvo(null); }}
                          onClick={() => {
                            if (!canAccessTask(task)) {
                              alert('Você não tem acesso a esta tarefa.');
                              return;
                            }
                            setSelectedTask(task);
                          }}
                          onComplete={() => onUpdateTask({ ...task, status: 'concluida', concluidaEm: new Date() })}
                        />
                      ))
                    ) : (
                      <div className="flex flex-col items-center justify-center py-16 text-gray-300">
                        <DocumentIcon className="h-12 w-12 mb-3" />
                        <p className="text-sm font-medium">Nenhuma tarefa</p>
                      </div>
                    )}
                    
                    {/* Add Task Button - apenas em Pendente */}
                    {status === 'pendente' && (
                      <button
                        onClick={() => setShowCreateModal(true)}
                        className="w-full py-3.5 border-2 border-dashed border-gray-200 rounded-xl text-gray-400 hover:text-indigo-600 hover:border-indigo-300 hover:bg-indigo-50/50 transition-all text-sm font-medium flex items-center justify-center gap-2"
                      >
                        <PlusIcon className="h-5 w-5" />
                        Adicionar
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Lista View */}
        {viewMode === 'lista' && (
          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            {/* Table Header - hidden on mobile */}
            <div className="hidden md:grid grid-cols-12 gap-4 px-4 py-3 bg-gray-50 border-b border-gray-200 text-xs font-medium text-gray-500 uppercase tracking-wider">
              <div className="col-span-4">Tarefa</div>
              <div className="col-span-2">Responsável</div>
              <div className="col-span-2">Status</div>
              <div className="col-span-2">Prazo</div>
              <div className="col-span-1">Prioridade</div>
              <div className="col-span-1 text-right">Ações</div>
            </div>
            
            {/* Table Body */}
            <div className="divide-y divide-gray-100">
              {filteredTasks.length > 0 ? (
                filteredTasks.map(task => {
                  const statusConfig = getStatusConfig(task.status);
                  const prioridadeConfig = getPrioridadeConfig(task.prioridade);
                  const responsavel = getColaboradorById(task.responsavelId);
                  const atrasada = isAtrasada(task);
                  const StatusIcon = statusConfig.icon;
                  
                  return (
                    <div 
                      key={task.id}
                      onClick={() => {
                        if (!canAccessTask(task)) {
                          alert('Você não tem acesso a esta tarefa.');
                          return;
                        }
                        setSelectedTask(task);
                      }}
                      className={`cursor-pointer transition-colors hover:bg-gray-50 ${
                        atrasada ? 'bg-red-50/50' : ''
                      }`}
                    >
                      {/* Desktop row */}
                      <div className="hidden md:grid grid-cols-12 gap-4 px-4 py-3">
                        {/* Tarefa */}
                        <div className="col-span-4 flex items-center gap-3">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              if (task.status !== 'concluida') {
                                onUpdateTask({ ...task, status: 'concluida', concluidaEm: new Date() });
                              }
                            }}
                            className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                              task.status === 'concluida'
                                ? 'bg-gray-700 border-gray-700 text-white'
                                : 'border-gray-300 hover:border-gray-500'
                            }`}
                          >
                            {task.status === 'concluida' && <CheckCircleIcon className="h-3 w-3" />}
                          </button>
                          <div className="min-w-0">
                            <p className={`font-medium text-gray-900 truncate ${task.status === 'concluida' ? 'line-through text-gray-400' : ''}`}>
                              {task.titulo}
                            </p>
                            {task.descricao && (
                              <p className="text-xs text-gray-500 truncate">{task.descricao}</p>
                            )}
                          </div>
                          {task.anexos && task.anexos.length > 0 && (
                            <PaperClipIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
                          )}
                        </div>
                        
                        {/* Responsável */}
                        <div className="col-span-2 flex items-center gap-2">
                          <div className="w-7 h-7 rounded-full bg-gray-200 flex items-center justify-center text-xs font-medium text-gray-600">
                            {responsavel?.nome?.charAt(0) || '?'}
                          </div>
                          <span className="text-sm text-gray-700 truncate">{responsavel?.nome || 'Sem responsável'}</span>
                        </div>
                        
                        {/* Status */}
                        <div className="col-span-2 flex items-center">
                          <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${statusConfig.color}`}>
                            <StatusIcon className="h-3.5 w-3.5" />
                            {statusConfig.label}
                          </span>
                        </div>
                        
                        {/* Prazo */}
                        <div className="col-span-2 flex items-center">
                          <span className={`text-sm ${atrasada ? 'text-red-600 font-medium' : 'text-gray-600'}`}>
                            {formatDateRelative(task.prazo)}
                          </span>
                        </div>
                        
                        {/* Prioridade */}
                        <div className="col-span-1 flex items-center gap-1.5">
                          <div className={`w-2 h-2 rounded-full ${prioridadeConfig.dot}`}></div>
                          <span className={`text-xs font-medium ${prioridadeConfig.color}`}>
                            {prioridadeConfig.label}
                          </span>
                        </div>
                        
                        {/* Ações */}
                        <div className="col-span-1 flex items-center justify-end gap-1">
                          <button 
                            onClick={(e) => {
                              e.stopPropagation();
                              if (!canAccessTask(task)) {
                                alert('Você não tem acesso a esta tarefa.');
                                return;
                              }
                              setSelectedTask(task);
                            }}
                            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                          >
                            <EyeIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </div>

                      {/* Mobile card */}
                      <div className="md:hidden px-3 py-3">
                        <div className="flex items-start gap-3">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              if (task.status !== 'concluida') {
                                onUpdateTask({ ...task, status: 'concluida', concluidaEm: new Date() });
                              }
                            }}
                            className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 transition-colors ${
                              task.status === 'concluida'
                                ? 'bg-gray-700 border-gray-700 text-white'
                                : 'border-gray-300 hover:border-gray-500'
                            }`}
                          >
                            {task.status === 'concluida' && <CheckCircleIcon className="h-3 w-3" />}
                          </button>
                          <div className="flex-1 min-w-0">
                            <p className={`font-medium text-sm text-gray-900 ${task.status === 'concluida' ? 'line-through text-gray-400' : ''}`}>
                              {task.titulo}
                            </p>
                            <div className="flex flex-wrap items-center gap-2 mt-1.5">
                              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${statusConfig.color}`}>
                                <StatusIcon className="h-3 w-3" />
                                {statusConfig.label}
                              </span>
                              <span className={`text-xs ${atrasada ? 'text-red-600 font-medium' : 'text-gray-500'}`}>
                                {formatDateRelative(task.prazo)}
                              </span>
                              <div className="flex items-center gap-1">
                                <div className={`w-1.5 h-1.5 rounded-full ${prioridadeConfig.dot}`}></div>
                                <span className={`text-[10px] font-medium ${prioridadeConfig.color}`}>{prioridadeConfig.label}</span>
                              </div>
                              {task.anexos && task.anexos.length > 0 && (
                                <PaperClipIcon className="h-3 w-3 text-gray-400" />
                              )}
                            </div>
                            <div className="flex items-center gap-1.5 mt-1.5">
                              <div className="w-5 h-5 rounded-full bg-gray-200 flex items-center justify-center text-[9px] font-medium text-gray-600">
                                {responsavel?.nome?.charAt(0) || '?'}
                              </div>
                              <span className="text-xs text-gray-600 truncate">{responsavel?.nome || 'Sem responsável'}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="px-4 py-12 text-center">
                  <DocumentIcon className="h-10 w-10 text-gray-300 mx-auto mb-3" />
                  <h3 className="text-sm font-medium text-gray-900 mb-1">Nenhuma tarefa encontrada</h3>
                  <p className="text-xs text-gray-500">
                    {searchTerm ? 'Tente ajustar sua busca' : 'Clique em "Nova Tarefa" para criar uma'}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Create Task Modal */}
        {showCreateModal && (
          <CreateTaskModal
            colaboradores={colaboradores}
            departamentos={departamentos}
            session={session}
            currentColaborador={currentColaborador}
            onClose={() => setShowCreateModal(false)}
            onCreate={onCreateTask}
          />
        )}

        {/* Task Detail Modal */}
        {selectedTask && (
          <TaskDetailModal
            task={selectedTask}
            colaboradores={colaboradores}
            session={session}
            onClose={() => setSelectedTask(null)}
            onUpdate={onUpdateTask}
            onDelete={onDeleteTask}
          />
        )}
      </div>
    </div>
  );
};

// Componente de Card para Kanban
interface TaskKanbanCardProps {
  task: Task;
  getColaboradorById: (id: string) => Colaborador | undefined;
  getPrioridadeConfig: (prioridade: TaskPriority) => { label: string; color: string; dot: string };
  isAtrasada: (task: Task) => boolean;
  formatDateRelative: (date: Date | string) => string;
  showLeaderNotice?: boolean;
  canComplete?: boolean;
  podeArrastar?: boolean;
  arrastando?: boolean;
  onDragStart?: () => void;
  onDragEnd?: () => void;
  onClick: () => void;
  onComplete: () => void;
}

const TaskKanbanCard = ({ 
  task, 
  getColaboradorById, 
  getPrioridadeConfig, 
  isAtrasada, 
  formatDateRelative,
  showLeaderNotice,
  canComplete = false,
  podeArrastar = false,
  arrastando = false,
  onDragStart,
  onDragEnd,
  onClick, 
  onComplete 
}: TaskKanbanCardProps) => {
  const responsavel = getColaboradorById(task.responsavelId);
  const prioridadeConfig = getPrioridadeConfig(task.prioridade);
  const atrasada = isAtrasada(task);
  
  return (
    <div 
      onClick={onClick}
      draggable={podeArrastar}
      onDragStart={(e) => {
        if (!podeArrastar) return;
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', task.id);
        onDragStart?.();
      }}
      onDragEnd={() => onDragEnd?.()}
      className={`bg-white rounded-xl border-2 p-4 hover:shadow-lg transition-all group overflow-hidden ${
        podeArrastar ? 'cursor-grab active:cursor-grabbing' : 'cursor-pointer'
      } ${arrastando ? 'opacity-40 scale-[0.98]' : ''} ${
        atrasada ? 'border-red-200 bg-gradient-to-br from-red-50/50 to-white' : 'border-gray-100 hover:border-indigo-200'
      }`}
    >
      {/* Header with Priority */}
      <div className="flex items-center justify-between mb-2.5">
        <div className={`flex items-center gap-2 px-2.5 py-1 rounded-full ${
          task.prioridade === 'urgente' ? 'bg-red-100' :
          task.prioridade === 'alta' ? 'bg-amber-100' :
          'bg-gray-100'
        }`}>
          <div className={`w-2 h-2 rounded-full ${prioridadeConfig.dot}`}></div>
          <span className={`text-[11px] font-semibold ${prioridadeConfig.color}`}>
            {prioridadeConfig.label}
          </span>
        </div>
        {task.nivelEscalonamentoAtual > 0 && (
          <span className="text-[10px] text-amber-600 font-semibold flex items-center gap-1 bg-amber-50 px-2 py-0.5 rounded-full">
            <ExclamationTriangleIcon className="h-3 w-3" />
            Nível {task.nivelEscalonamentoAtual}
          </span>
        )}
      </div>
      
      {/* Title */}
      <h4 className={`text-sm font-semibold text-gray-900 mb-2 line-clamp-2 group-hover:text-indigo-700 transition-colors ${
        task.status === 'concluida' ? 'line-through text-gray-400' : ''
      }`}>
        {task.titulo}
      </h4>
      
      {/* Description */}
      {task.descricao && (
        <p className="text-xs text-gray-500 mb-3 line-clamp-2">{task.descricao}</p>
      )}
      
      {/* Footer */}
      <div className="pt-3 border-t border-gray-100 space-y-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            {/* Avatar */}
            <div className="w-7 h-7 rounded-full bg-gradient-to-br from-indigo-500 to-indigo-700 flex items-center justify-center text-[11px] font-bold text-white shadow-sm flex-shrink-0">
              {responsavel?.nome?.charAt(0) || '?'}
            </div>
            {/* Prazo */}
            <span className={`text-[11px] font-medium ${atrasada ? 'text-red-600' : 'text-gray-500'}`}>
              {formatDateRelative(task.prazo)}
            </span>
          </div>
          
          <div className="flex items-center gap-1.5">
            {task.anexos && task.anexos.length > 0 && (
              <span className="flex items-center gap-1 text-gray-400 bg-gray-50 px-1.5 py-0.5 rounded-full">
                <PaperClipIcon className="h-3 w-3" />
                <span className="text-[10px] font-medium">{task.anexos.length}</span>
              </span>
            )}
            {task.comentarios && task.comentarios.length > 0 && (
              <span className="flex items-center gap-1 text-gray-400 bg-gray-50 px-1.5 py-0.5 rounded-full">
                <ChatBubbleLeftIcon className="h-3 w-3" />
                <span className="text-[10px] font-medium">{task.comentarios.length}</span>
              </span>
            )}
            
            {/* Complete Button - apenas se tem permissão */}
            {task.status !== 'concluida' && canComplete && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onComplete();
                }}
                className="p-1 text-gray-300 hover:text-emerald-500 hover:bg-emerald-50 rounded-lg transition-all"
                title="Marcar como concluída"
              >
                <CheckCircleIcon className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
        
        {/* Indicador de aguardando prazo - linha separada */}
        {showLeaderNotice && (
          <div className="flex items-center gap-1 text-amber-700 bg-amber-100 px-2 py-1 rounded-lg text-[10px] font-semibold">
            <ExclamationTriangleIcon className="h-3 w-3 flex-shrink-0" />
            <span className="truncate">Aguardando prazo</span>
          </div>
        )}
      </div>
    </div>
  );
};

// Modal de Criação de Tarefa
interface CreateTaskModalProps {
  colaboradores: Colaborador[];
  departamentos: Departamento[];
  session: UserSession;
  currentColaborador?: Colaborador;
  onClose: () => void;
  onCreate: (task: Omit<Task, 'id' | 'criadaEm' | 'atualizadaEm' | 'historico' | 'nivelEscalonamentoAtual'>, arquivos?: File[]) => Promise<void> | void;
}

const CreateTaskModal = ({ colaboradores, departamentos, session, currentColaborador, onClose, onCreate }: CreateTaskModalProps) => {
  const [titulo, setTitulo] = useState('');
  const [descricao, setDescricao] = useState('');
  const [departamentoId, setDepartamentoId] = useState('');
  const [responsavelId, setResponsavelId] = useState('');
  const [prazo, setPrazo] = useState('');
  const [prioridade, setPrioridade] = useState<TaskPriority>('normal');
  const [observadoresIds, setObservadoresIds] = useState<string[]>([]);
  const [camposConfig, setCamposConfig] = useState<TaskCampoConfig[]>([]);
  const [showConfigCampos, setShowConfigCampos] = useState(false);
  const [arquivos, setArquivos] = useState<File[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Obter superior do responsável selecionado (para mostrar info de escalonamento)
  const getSuperior = (colaboradorId: string): Colaborador | null => {
    const colaborador = colaboradores.find(c => c.id === colaboradorId);
    if (colaborador?.superiorId) {
      return colaboradores.find(c => c.id === colaborador.superiorId) || null;
    }
    return null;
  };

  const superior = responsavelId ? getSuperior(responsavelId) : null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!titulo.trim() || !responsavelId || !prazo) return;

    setSubmitting(true);
    try {
      await onCreate({
        titulo: titulo.trim(),
        descricao: descricao.trim() || undefined,
        solicitanteId: currentColaborador?.id || session.usuario.id,
        responsavelId,
        prazo: new Date(prazo),
        status: 'pendente',
        prioridade,
        observadoresIds,
        camposConfig,
        empresaId: session.empresa?.id || ''
      }, arquivos);
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const handlePickFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return;

    // 🖼️ Comprimir imagens (se forem fotos grandes de celular)
    const picked = await compressImagesIfNeeded(files);
    const MAX_ANEXOS = 10;

    const merged = [...arquivos, ...picked].slice(0, MAX_ANEXOS);
    setArquivos(merged);

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">Nova Tarefa</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg transition-colors">
            <XMarkIcon className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-5 space-y-4 overflow-y-auto max-h-[calc(90vh-130px)]">
          {/* Título */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1.5">Título *</label>
            <input
              type="text"
              value={titulo}
              onChange={(e) => setTitulo(e.target.value)}
              placeholder="O que precisa ser feito?"
              className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-400/30 focus:border-gray-400"
              required
            />
          </div>

          {/* Descrição */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1.5">Descrição</label>
            <textarea
              value={descricao}
              onChange={(e) => setDescricao(e.target.value)}
              placeholder="Detalhes adicionais sobre a tarefa..."
              rows={3}
              className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-400/30 focus:border-gray-400 resize-none"
            />
          </div>

          {/* Anexos */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="block text-xs font-medium text-gray-700">Anexos</label>
              <span className="text-[10px] text-gray-400">{arquivos.length}/10</span>
            </div>

            {arquivos.length > 0 && (
              <div className="space-y-2 mb-3">
                {arquivos.map((f, idx) => (
                  <div key={`${f.name}-${idx}`} className="flex items-center gap-3 bg-gray-50 rounded-lg p-2">
                    <DocumentIcon className="h-5 w-5 text-gray-500" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{f.name}</p>
                      <p className="text-[10px] text-gray-400">{formatFileSize(f.size)}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setArquivos(prev => prev.filter((_, i) => i !== idx))}
                      className="p-1.5 hover:bg-red-100 rounded-lg transition-colors"
                      title="Remover"
                    >
                      <TrashIcon className="h-4 w-4 text-red-500" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <input
              ref={fileInputRef}
              type="file"
              multiple
              onChange={(e) => handlePickFiles(e.target.files)}
              className="hidden"
              accept=".jpg,.jpeg,.png,.gif,.webp,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.zip,.rar"
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={arquivos.length >= 10 || submitting}
              className={`w-full flex items-center justify-center gap-2 px-3 py-3 border-2 border-dashed rounded-lg transition-colors ${
                arquivos.length >= 10 || submitting
                  ? 'border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed'
                  : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50 text-gray-600'
              }`}
            >
              <PaperClipIcon className="h-4 w-4" />
              <span className="text-sm">{arquivos.length >= 10 ? 'Limite de anexos atingido' : 'Adicionar anexos'}</span>
            </button>
            <p className="text-[10px] text-gray-400 text-center mt-1">
              Os anexos sobem após criar a tarefa • Máx. 10MB/arquivo
            </p>
          </div>

          {/* Departamento */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1.5">Departamento *</label>
            <select
              value={departamentoId}
              onChange={(e) => { setDepartamentoId(e.target.value); setResponsavelId(''); }}
              className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-400/30 focus:border-gray-400"
              required
            >
              <option value="">Selecione um departamento</option>
              {departamentos.map(d => (
                <option key={d.id} value={d.id}>{d.nome}</option>
              ))}
            </select>
          </div>

          {/* Responsável */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1.5">Responsável *</label>
            <select
              value={responsavelId}
              onChange={(e) => setResponsavelId(e.target.value)}
              className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-400/30 focus:border-gray-400"
              required
              disabled={!departamentoId}
            >
              <option value="">{departamentoId ? 'Selecione um colaborador' : 'Selecione um departamento primeiro'}</option>
              {colaboradores
                .filter(c => c.departamentoId === departamentoId)
                .map(c => (
                <option key={c.id} value={c.id}>{c.nome}</option>
              ))}
            </select>
            
            {/* Info de Escalonamento Automático */}
            {responsavelId && (
              <div className="mt-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-start gap-2">
                  <ExclamationTriangleIcon className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div className="text-xs text-amber-800">
                    <p className="font-medium">Escalonamento automático</p>
                    {superior ? (
                      <p className="mt-0.5">
                        Se não concluída no prazo, será escalonada automaticamente para <strong>{superior.nome}</strong> (superior hierárquico)
                      </p>
                    ) : (
                      <p className="mt-0.5 text-amber-600">
                        Este colaborador não possui superior cadastrado no organograma
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Mencionados (observadores) */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1.5">
              Mencionar pessoas
              <span className="ml-1 font-normal text-gray-400">(quem mais pode ver a tarefa)</span>
            </label>
            <select
              value=""
              onChange={(e) => {
                const id = e.target.value;
                if (id && !observadoresIds.includes(id)) setObservadoresIds([...observadoresIds, id]);
              }}
              className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-400/30 focus:border-gray-400"
            >
              <option value="">Adicionar pessoa...</option>
              {colaboradores
                .filter(c => c.id !== responsavelId && !observadoresIds.includes(c.id))
                .map(c => (
                  <option key={c.id} value={c.id}>{c.nome}</option>
                ))}
            </select>

            {observadoresIds.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {observadoresIds.map(id => {
                  const c = colaboradores.find(col => col.id === id);
                  return (
                    <span key={id} className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-700 rounded-full text-[11px] font-medium">
                      @{c?.nome || id}
                      <button
                        type="button"
                        onClick={() => setObservadoresIds(observadoresIds.filter(o => o !== id))}
                        className="hover:text-red-600"
                        title="Remover"
                      >
                        <XMarkIcon className="h-3 w-3" />
                      </button>
                    </span>
                  );
                })}
              </div>
            )}
            <p className="text-[10px] text-gray-400 mt-1">
              Só solicitante, responsável, mencionados e o líder do responsável enxergam esta tarefa.
            </p>
          </div>

          {/* Campos personalizados já na criação */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="block text-xs font-medium text-gray-700">
                Campos personalizados
                <span className="ml-1 font-normal text-gray-400">(opcional)</span>
              </label>
              <span className="text-[10px] text-gray-400">{camposConfig.length} campo(s)</span>
            </div>

            {camposConfig.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-2">
                {camposConfig.map(c => (
                  <span key={c.id} className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-700 rounded-lg text-[11px] font-medium">
                    {c.nome}
                    {c.obrigatorio && <span className="text-red-500">*</span>}
                  </span>
                ))}
              </div>
            )}

            <button
              type="button"
              onClick={() => setShowConfigCampos(true)}
              className="w-full flex items-center justify-center gap-2 px-3 py-2.5 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-gray-400 hover:bg-gray-50 transition-colors text-sm"
            >
              <Cog6ToothIcon className="h-4 w-4" />
              {camposConfig.length > 0 ? 'Editar campos' : 'Adicionar campos'}
            </button>
            <p className="text-[10px] text-gray-400 mt-1">
              Dá pra montar os campos agora e continuar mexendo depois, dentro da tarefa.
            </p>
          </div>

          {/* Prazo e Prioridade */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5">Prazo *</label>
              <input
                type="datetime-local"
                value={prazo}
                onChange={(e) => setPrazo(e.target.value)}
                className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-400/30 focus:border-gray-400"
                required
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5">Prioridade</label>
              <select
                value={prioridade}
                onChange={(e) => setPrioridade(e.target.value as TaskPriority)}
                className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-400/30 focus:border-gray-400"
              >
                <option value="baixa">Baixa</option>
                <option value="normal">Normal</option>
                <option value="alta">Alta</option>
                <option value="urgente">Urgente</option>
              </select>
            </div>
          </div>
        </form>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-gray-100 flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors"
          >
            Cancelar
          </button>
          <button
            onClick={handleSubmit}
            disabled={!titulo.trim() || !responsavelId || !prazo || submitting}
            className="px-4 py-2 bg-gradient-to-r from-gray-900 to-black text-white text-sm font-medium rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? 'Criando...' : 'Criar Tarefa'}
          </button>
        </div>
      </div>

      {showConfigCampos && (
        <ConfigCamposModal
          campos={camposConfig}
          onSave={(novosCampos) => {
            setCamposConfig(novosCampos);
            setShowConfigCampos(false);
          }}
          onClose={() => setShowConfigCampos(false)}
        />
      )}
    </div>
  );
};

// Modal de Detalhes da Tarefa
interface TaskDetailModalProps {
  task: Task;
  colaboradores: Colaborador[];
  session: UserSession;
  onClose: () => void;
  onUpdate: (task: Task) => void;
  onDelete: (taskId: string) => void;
}

const TaskDetailModal = ({ task, colaboradores, session, onClose, onUpdate, onDelete }: TaskDetailModalProps) => {
  const [editMode, setEditMode] = useState(false);
  const [titulo, setTitulo] = useState(task.titulo);
  const [descricao, setDescricao] = useState(task.descricao || '');
  const [status, setStatus] = useState(task.status);
  const [prazo, setPrazo] = useState(new Date(task.prazo).toISOString().slice(0, 16));
  const [responsavelId, setResponsavelId] = useState(task.responsavelId);
  const [prioridade, setPrioridade] = useState(task.prioridade);
  const [observadoresIds, setObservadoresIds] = useState<string[]>(task.observadoresIds || []);
  const [novoComentario, setNovoComentario] = useState('');
  const [comentarios, setComentarios] = useState(task.comentarios || []);
  const [editandoComentarioId, setEditandoComentarioId] = useState<string | null>(null);
  const [textoEditandoComentario, setTextoEditandoComentario] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [anexos, setAnexos] = useState<TaskAnexo[]>(task.anexos || []);
  const [justificativa, setJustificativa] = useState(task.justificativaAtraso || '');
  const [justificativaAtrasoLocal, setJustificativaAtrasoLocal] = useState(task.justificativaAtraso || '');
  const [showJustificativaModal, setShowJustificativaModal] = useState(false);
  const [showRedefinirPrazoModal, setShowRedefinirPrazoModal] = useState(false);
  const [novoPrazo, setNovoPrazo] = useState('');
  const [motivoProrrogacao, setMotivoProrrogacao] = useState('');
  const [camposValores, setCamposValores] = useState<Record<string, any>>(task.camposValores || {});
  const [novoItemProgresso, setNovoItemProgresso] = useState<Record<string, string>>({});
  const [comentarioItem, setComentarioItem] = useState<{ campoId: string; itemId: string; texto: string } | null>(null);
  const [showConfigCampos, setShowConfigCampos] = useState(false);
  const [camposConfig, setCamposConfig] = useState(task.camposConfig || []);
  const [draggedItemIndex, setDraggedItemIndex] = useState<number | null>(null);
  const [draggedCampoId, setDraggedCampoId] = useState<string | null>(null);
  const [linhaTempoMinimizada, setLinhaTempoMinimizada] = useState(true);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Sincronizar estados locais quando a task for atualizada (ex: após redefinir prazo)
  useEffect(() => {
    setJustificativa(task.justificativaAtraso || '');
    setJustificativaAtrasoLocal(task.justificativaAtraso || '');
    setPrazo(new Date(task.prazo).toISOString().slice(0, 16));
    setStatus(task.status);
    setComentarios(task.comentarios || []);
    setAnexos(task.anexos || []);
    setCamposValores(task.camposValores || {});
    setCamposConfig(task.camposConfig || []);
    setObservadoresIds(task.observadoresIds || []);
  }, [task]);

  const getColaboradorById = (id: string) =>
    colaboradores.find(c =>
      c.id === id ||
      c.userId === id ||
      (c as any).usuarioId === id ||
      c.email?.toLowerCase() === String(id).toLowerCase()
    );
  const solicitante = getColaboradorById(task.solicitanteId);
  const responsavel = getColaboradorById(editMode ? responsavelId : task.responsavelId);
  const currentColaborador = colaboradores.find(c =>
    c.email?.toLowerCase() === session.usuario.email?.toLowerCase() ||
    c.id === session.usuario.id ||
    c.userId === session.usuario.id ||
    (c as any).usuarioId === session.usuario.id
  );

  const userIdCandidates = [
    session.usuario.id,
    session.usuario.email,
    currentColaborador?.id,
    currentColaborador?.userId,
    (currentColaborador as any)?.usuarioId,
    currentColaborador?.email
  ].filter(Boolean) as string[];

  const matchesUser = (targetId?: string) =>
    !!targetId && userIdCandidates.some(id => String(id).toLowerCase() === String(targetId).toLowerCase());

  const isResponsavel =
    matchesUser(task.responsavelId) ||
    matchesUser(responsavel?.userId) ||
    matchesUser((responsavel as any)?.usuarioId) ||
    matchesUser(responsavel?.email) ||
    (responsavel?.nome && responsavel.nome === session.usuario.nome);

  const isSolicitante =
    matchesUser(task.solicitanteId) ||
    matchesUser(solicitante?.userId) ||
    matchesUser((solicitante as any)?.usuarioId) ||
    matchesUser(solicitante?.email) ||
    (solicitante?.nome && solicitante.nome === session.usuario.nome);

  // Verificar se o usuário atual é o líder/superior do responsável
  const isLiderDoResponsavel = (() => {
    if (!responsavel?.superiorId || !currentColaborador) return false;
    return responsavel.superiorId === currentColaborador.id ||
           responsavel.superiorId === currentColaborador.userId ||
           responsavel.superiorId === (currentColaborador as any)?.usuarioId;
  })();

  // Verificar permissões de edição: apenas admin e criador (solicitante) podem editar
  const isAdmin = session.usuario.role === 'administrator';
  const canEdit = isAdmin || isSolicitante;
  
  // Quem pode justificar: responsável, líder do responsável ou admin
  const canJustify = isResponsavel || isLiderDoResponsavel || isAdmin;
  
  // Quem pode mudar status: responsável, solicitante, líder do responsável ou admin
  const canChangeStatus = isResponsavel || isSolicitante || isLiderDoResponsavel || isAdmin;

  // Quem pode REDEFINIR PRAZO: solicitante, líder do responsável ou admin
  // MAS NUNCA o próprio responsável (ele só justifica, não redefine)
  const canRedefinirPrazo = (isSolicitante || isLiderDoResponsavel || isAdmin) && !isResponsavel;

  // Verificar se está atrasada
  const isAtrasada = new Date(task.prazo) < new Date() && !['concluida', 'cancelada'].includes(task.status);
  const temJustificativa = !!justificativaAtrasoLocal;
  
  // Calcular dias de atraso
  const diasAtraso = isAtrasada 
    ? Math.ceil((new Date().getTime() - new Date(task.prazo).getTime()) / (1000 * 60 * 60 * 24))
    : 0;
  
  // Contar quantas vezes o prazo foi prorrogado
  const qtdProrrogacoes = (task.historico || []).filter(h => (h.acao ?? (h as any).tipo) === 'prazo_alterado').length;
  
  // Extrair a última prorrogação com justificativa
  const ultimaProrrogacao = (task.historico || [])
    .filter(h => (h.acao ?? (h as any).tipo) === 'prazo_alterado')
    .pop();
  const motivoUltimaProrrogacao = ultimaProrrogacao?.descricao?.includes('Motivo:')
    ? ultimaProrrogacao.descricao.split('Motivo:')[1]?.trim()
    : null;

  // Funções para campos personalizados
  const handleCampoChange = (campoId: string, valor: any) => {
    const novosValores = { ...camposValores, [campoId]: valor };
    setCamposValores(novosValores);
    
    // Encontrar nome do campo para o histórico
    const campo = camposConfig.find(c => c.id === campoId);
    const nomeCampo = campo?.nome || 'Campo';
    const usuarioNome = currentColaborador?.nome || session.usuario.nome || 'Usuário';
    
    // Não registrar histórico para lista_progresso (tem muitas alterações internas)
    const novoHistorico = campo?.tipo !== 'lista_progresso' ? [
      ...((task.historico || []) as any[]),
      {
        id: `hist_${Date.now()}`,
        acao: 'campo_alterado',
        descricao: `Campo "${nomeCampo}" foi alterado`,
        realizadoPor: usuarioNome,
        realizadoEm: new Date().toISOString()
      }
    ] : task.historico;
    
    onUpdate({
      ...task,
      camposValores: novosValores,
      historico: novoHistorico as any,
      atualizadaEm: new Date()
    });
  };

  // Funções para Lista de Progresso
  const adicionarItemProgresso = (campoId: string, nome: string) => {
    if (!nome.trim()) return;
    const itensAtuais = (camposValores[campoId] || []) as ItemProgresso[];
    const novoItem: ItemProgresso = {
      id: `item_${Date.now()}`,
      nome: nome.trim(),
      concluido: false,
      ordem: itensAtuais.length
    };
    const novosItens = [...itensAtuais, novoItem];
    handleCampoChange(campoId, novosItens);
    setNovoItemProgresso(prev => ({ ...prev, [campoId]: '' }));
  };

  const toggleItemProgresso = (campoId: string, itemId: string) => {
    const itens = (camposValores[campoId] || []) as ItemProgresso[];
    const novosItens = itens.map(item => 
      item.id === itemId 
        ? { ...item, concluido: !item.concluido, concluidoEm: !item.concluido ? new Date() : undefined }
        : item
    );
    handleCampoChange(campoId, novosItens);
  };

  const removerItemProgresso = (campoId: string, itemId: string) => {
    const itens = (camposValores[campoId] || []) as ItemProgresso[];
    handleCampoChange(campoId, itens.filter(i => i.id !== itemId));
  };

  const reordenarItemProgresso = (campoId: string, fromIndex: number, toIndex: number) => {
    const itens = [...(camposValores[campoId] || [])] as ItemProgresso[];
    const [itemMovido] = itens.splice(fromIndex, 1);
    itens.splice(toIndex, 0, itemMovido);
    
    // Atualizar ordem
    const itensComNovaOrdem = itens.map((item, index) => ({ ...item, ordem: index }));
    handleCampoChange(campoId, itensComNovaOrdem);
  };

  const adicionarComentarioItem = (campoId: string, itemId: string, texto: string) => {
    const itens = (camposValores[campoId] || []) as ItemProgresso[];
    const novosItens = itens.map(item =>
      item.id === itemId ? { ...item, comentario: texto || undefined } : item
    );
    handleCampoChange(campoId, novosItens);
    setComentarioItem(null);
  };

  const calcularProgressoLista = (campoId: string) => {
    const itens = (camposValores[campoId] || []) as ItemProgresso[];
    const total = itens.length;
    const concluidos = itens.filter(i => i.concluido).length;
    const porcentagem = total > 0 ? Math.round((concluidos / total) * 100) : 0;
    return { total, concluidos, porcentagem };
  };

  // Upload de anexos
  const handleFileUpload = async (files: FileList | null) => {
    if (!files || files.length === 0) return;

    setUploading(true);
    try {
      // 🖼️ Comprimir imagens (se forem fotos grandes de celular)
      const compressed = await compressImagesIfNeeded(files);
      const formData = new FormData();
      compressed.forEach(file => {
        formData.append('arquivos', file);
      });
      formData.append('enviadoPor', currentColaborador?.id || session.usuario.id);

      const response = await fetch(`/api/tasks/${task.id}/anexos`, {
        method: 'POST',
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        if (data.status === 'success' && data.anexos) {
          setAnexos(prev => [...prev, ...data.anexos]);
          // Atualizar task pai
          onUpdate({
            ...task,
            anexos: [...anexos, ...data.anexos],
            atualizadaEm: new Date()
          });
        }
      } else {
        const error = await response.json();
        alert(error.message || 'Erro ao enviar arquivos');
      }
    } catch (error) {
      console.error('Erro no upload:', error);
      alert('Erro ao enviar arquivos');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Remover anexo
  const handleRemoveAnexo = async (anexoId: string) => {
    if (!confirm('Remover este anexo?')) return;

    try {
      const response = await fetch(`/api/tasks/${task.id}/anexos/${anexoId}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ removidoPor: currentColaborador?.id || session.usuario.id })
      });

      if (response.ok) {
        const novosAnexos = anexos.filter(a => a.id !== anexoId);
        setAnexos(novosAnexos);
        onUpdate({
          ...task,
          anexos: novosAnexos,
          atualizadaEm: new Date()
        });
      }
    } catch (error) {
      console.error('Erro ao remover anexo:', error);
      alert('Erro ao remover anexo');
    }
  };

  // Helper para formatar tamanho de arquivo
  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Helper para ícone de arquivo
  const getFileIcon = (tipo: string) => {
    if (tipo.startsWith('image/')) return <PhotoIcon className="h-5 w-5 text-gray-500" />;
    return <DocumentIcon className="h-5 w-5 text-gray-500" />;
  };

  // Quem pode mencionar: quem edita (admin/solicitante) ou o proprio responsavel
  const canMencionar = canEdit || isResponsavel;

  // Campos dinâmicos: o responsável também monta/ajusta os campos da tarefa dele,
  // não só o solicitante e o admin.
  const canEditCampos = canEdit || isResponsavel;

  const salvarObservadores = (novos: string[]) => {
    setObservadoresIds(novos);
    onUpdate({ ...task, observadoresIds: novos, atualizadaEm: new Date() });
  };

  const handleSave = () => {
    onUpdate({
      ...task,
      titulo,
      descricao: descricao || undefined,
      status,
      prazo: new Date(prazo),
      responsavelId,
      prioridade,
      observadoresIds,
      camposValores,
      comentarios,
      atualizadaEm: new Date()
    });
    setEditMode(false);
  };

  const handleAddComentario = () => {
    if (!novoComentario.trim()) return;

    const currentColaborador = colaboradores.find(c =>
      c.email === session.usuario.email ||
      c.id === session.usuario.id ||
      c.userId === session.usuario.id
    );
    const comentario = {
      id: Date.now().toString(),
      texto: novoComentario.trim(),
      autorId: currentColaborador?.id || currentColaborador?.userId || session.usuario.id,
      autorNome: currentColaborador?.nome || session.usuario.nome,
      autorEmail: currentColaborador?.email || session.usuario.email,
      criadoEm: new Date()
    };

    const novosComentarios = [...comentarios, comentario];
    setComentarios(novosComentarios);
    
    onUpdate({
      ...task,
      comentarios: novosComentarios,
      atualizadaEm: new Date()
    });

    setNovoComentario('');
  };

  const handleDeleteComentario = (comentarioId: string) => {
    if (!confirm('Deseja excluir este comentário?')) return;
    
    const novosComentarios = comentarios.filter(c => c.id !== comentarioId);
    setComentarios(novosComentarios);
    
    onUpdate({
      ...task,
      comentarios: novosComentarios,
      atualizadaEm: new Date()
    });
  };

  const handleEditComentario = (comentarioId: string, novoTexto: string) => {
    const novosComentarios = comentarios.map(c =>
      c.id === comentarioId ? { ...c, texto: novoTexto, editadoEm: new Date() } : c
    );
    setComentarios(novosComentarios);
    
    onUpdate({
      ...task,
      comentarios: novosComentarios,
      atualizadaEm: new Date()
    });
  };

  const canEditComentario = (comentario: any) => {
    // Admin pode editar qualquer comentário
    if (isAdmin) return true;
    
    // Editor só pode editar seus próprios comentários
    const currentColaborador = colaboradores.find(c =>
      c.email === session.usuario.email ||
      c.id === session.usuario.id ||
      c.userId === session.usuario.id
    );
    
    return (
      comentario.autorId === currentColaborador?.id ||
      comentario.autorId === currentColaborador?.userId ||
      comentario.autorId === session.usuario.id ||
      comentario.autorEmail === session.usuario.email
    );
  };

  const handleStatusChange = (newStatus: TaskStatus) => {
    setStatus(newStatus);
    onUpdate({
      ...task,
      status: newStatus,
      concluidaEm: newStatus === 'concluida' ? new Date() : undefined,
      atualizadaEm: new Date()
    });
  };

  const handleDelete = () => {
    onDelete(task.id);
    onClose();
  };

  const formatDateTime = (date?: Date | string) => {
    if (!date) return 'Data não definida';
    const d = new Date(date);
    if (isNaN(d.getTime())) return 'Data inválida';
    return d.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      
      <div className="relative bg-white shadow-2xl w-screen h-screen md:w-screen md:h-screen overflow-hidden flex flex-col">
        {/* ========== HEADER COMPLETO COM TUDO ========== */}
        <div className="border-b border-gray-100 bg-white flex-shrink-0">
          {/* Linha 1: Título, Prioridade, Descrição, Status e Ações */}
          <div className="px-3 py-1.5 sm:px-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-3">
            <div className="flex items-center gap-2 flex-wrap">
              {editMode ? (
                <input
                  type="text"
                  value={titulo}
                  onChange={(e) => setTitulo(e.target.value)}
                  className="text-sm sm:text-lg font-bold text-gray-900 bg-gray-50 border border-gray-200 rounded-lg px-3 py-1 focus:outline-none focus:ring-2 focus:ring-gray-300 w-full sm:w-auto"
                />
              ) : (
                <h2 className="text-sm sm:text-lg font-bold text-gray-900 line-clamp-2 sm:line-clamp-none">{task.titulo}</h2>
              )}
              
              {/* Prioridade */}
              {editMode ? (
                <select
                  value={prioridade}
                  onChange={(e) => setPrioridade(e.target.value as TaskPriority)}
                  className="px-2 py-0.5 rounded text-[11px] font-semibold bg-gray-100 text-gray-700 border border-gray-300 focus:outline-none focus:ring-1 focus:ring-gray-400"
                >
                  <option value="baixa">Baixa</option>
                  <option value="normal">Normal</option>
                  <option value="alta">Alta</option>
                  <option value="urgente">Urgente</option>
                </select>
              ) : (
                <span className={`px-2 py-0.5 rounded text-[11px] font-semibold ${
                  task.prioridade === 'urgente' ? 'bg-red-100 text-red-700' :
                  task.prioridade === 'alta' ? 'bg-amber-100 text-amber-700' :
                  task.prioridade === 'normal' ? 'bg-gray-100 text-gray-700' :
                  'bg-gray-50 text-gray-500'
                }`}>
                  {task.prioridade.charAt(0).toUpperCase() + task.prioridade.slice(1)}
                </span>
              )}
              
              {/* Outras tags */}
              {isAtrasada && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-100 text-red-700 rounded text-[11px] font-semibold">
                  <ExclamationTriangleIcon className="h-3 w-3" />
                  {diasAtraso}d atraso
                </span>
              )}
              
              {qtdProrrogacoes > 0 && (
                <span 
                  className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-[11px] font-semibold cursor-help"
                  title={motivoUltimaProrrogacao ? `Motivo: ${motivoUltimaProrrogacao}` : 'Prazo foi prorrogado'}
                >
                  <ClockIcon className="h-3 w-3" />
                  {qtdProrrogacoes}x prorrogado
                </span>
              )}
              
              {task.nivelEscalonamentoAtual > 0 && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-100 text-amber-700 rounded text-[11px] font-semibold">
                  <ArrowUpIcon className="h-3 w-3" />
                  Nível {task.nivelEscalonamentoAtual}
                </span>
              )}
              
              {/* Motivo da última prorrogação */}
              {motivoUltimaProrrogacao && (
                <div className="w-full mt-1 px-2 py-1.5 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-[10px] text-blue-600 font-medium">
                    📝 Motivo da prorrogação: <span className="text-blue-800">{motivoUltimaProrrogacao}</span>
                  </p>
                </div>
              )}
            </div>
            
            {/* Descrição (centro) */}
            <div className="hidden sm:block flex-1 mx-3 min-w-0">
              {editMode ? (
                <textarea
                  value={descricao}
                  onChange={(e) => setDescricao(e.target.value)}
                  rows={1}
                  className="w-full px-1.5 py-1 text-[11px] border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-gray-300 resize-none bg-gray-50"
                  placeholder="Descrição..."
                />
              ) : (
                <p className="text-[11px] text-gray-600 line-clamp-1">
                  {task.descricao || <span className="text-gray-400 italic">Sem descrição</span>}
                </p>
              )}
            </div>
            
            {/* Status - apenas quem tem permissão pode alterar */}
            <div className="flex flex-wrap gap-1 sm:gap-1.5 overflow-x-auto pb-1 sm:pb-0">
              {(['pendente', 'em_andamento', 'aguardando', 'concluida', 'cancelada'] as TaskStatus[]).map(s => {
                const cfg = {
                  pendente: { label: 'Pendente', bg: 'bg-gray-100', active: 'bg-gray-200 ring-2 ring-gray-500' },
                  em_andamento: { label: 'Em Andamento', bg: 'bg-slate-100 text-slate-700', active: 'bg-slate-200 ring-2 ring-slate-400' },
                  aguardando: { label: 'Aguardando', bg: 'bg-stone-100 text-stone-700', active: 'bg-stone-200 ring-2 ring-stone-400' },
                  concluida: { label: 'Concluída', bg: 'bg-zinc-200 text-zinc-700', active: 'bg-zinc-300 ring-2 ring-zinc-500' },
                  cancelada: { label: 'Cancelada', bg: 'bg-neutral-200 text-neutral-600', active: 'bg-neutral-300 ring-2 ring-neutral-400' }
                }[s];
                return (
                  <button
                    key={s}
                    onClick={() => canChangeStatus && handleStatusChange(s)}
                    disabled={!canChangeStatus}
                    className={`px-2.5 py-1 sm:px-4 sm:py-1.5 rounded-lg text-[10px] sm:text-xs font-semibold transition-all whitespace-nowrap ${
                      status === s ? cfg.active : cfg.bg + ' hover:opacity-80'
                    } ${!canChangeStatus ? 'opacity-50 cursor-not-allowed' : ''}`}
                    title={!canChangeStatus ? 'Você não tem permissão para alterar o status' : ''}
                  >
                    {cfg.label}
                  </button>
                );
              })}
            </div>
            
            {/* Botões de ação */}
            <div className="flex items-center gap-1">
              {!editMode && canEdit && (
                <>
                  <button onClick={() => setEditMode(true)} className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg">
                    <PencilIcon className="h-4 w-4" />
                  </button>
                  <button onClick={() => setShowDeleteConfirm(true)} className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg">
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </>
              )}
              <button onClick={onClose} className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg">
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
          </div>
          
          {/* Linha 2: Cards de Info */}
          <div className="px-3 pb-1.5 sm:px-6">
            {/* Cards de Pessoas e Datas */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-1.5">
              {/* Solicitante */}
              <div className="flex items-center gap-2 bg-gradient-to-br from-slate-50 to-white rounded-xl px-3 py-2 border border-slate-100 shadow-sm hover:shadow transition-shadow">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-slate-500 to-slate-700 flex items-center justify-center text-[11px] font-bold text-white flex-shrink-0 shadow-sm">
                  {solicitante?.nome?.charAt(0) || '?'}
                </div>
                <div className="min-w-0">
                  <p className="text-[9px] text-slate-400 uppercase tracking-wide font-medium">Solicitante</p>
                  <p className="text-xs font-semibold text-gray-800 truncate">{solicitante?.nome || 'Desconhecido'}</p>
                </div>
              </div>
              
              {/* Responsável */}
              <div className="flex items-center gap-2 bg-gradient-to-br from-gray-50 to-white rounded-xl px-3 py-2 border border-gray-100 shadow-sm hover:shadow transition-shadow">
                {editMode ? (
                  <div className="flex-1">
                    <p className="text-[8px] text-gray-500 uppercase font-semibold mb-0">Responsável</p>
                    <select
                      value={responsavelId}
                      onChange={(e) => setResponsavelId(e.target.value)}
                      className="w-full text-[11px] font-medium text-gray-900 bg-white border border-gray-300 rounded px-1 py-0 focus:outline-none focus:ring-1 focus:ring-gray-400"
                    >
                      {colaboradores.map(c => (
                        <option key={c.id} value={c.id}>{c.nome}</option>
                      ))}
                    </select>
                  </div>
                ) : (
                  <>
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-500 to-indigo-700 flex items-center justify-center text-[11px] font-bold text-white flex-shrink-0 shadow-sm">
                      {responsavel?.nome?.charAt(0) || '?'}
                    </div>
                    <div className="min-w-0">
                      <p className="text-[9px] text-gray-400 uppercase tracking-wide font-medium">Responsável</p>
                      <p className="text-xs font-semibold text-gray-800 truncate">{responsavel?.nome || 'Não atribuído'}</p>
                    </div>
                  </>
                )}
              </div>
              
              {/* Prazo */}
              <div className={`rounded-xl px-3 py-2 border shadow-sm hover:shadow transition-shadow ${isAtrasada ? 'bg-gradient-to-br from-red-50 to-white border-red-100' : 'bg-gradient-to-br from-amber-50 to-white border-amber-100'}`}>
                <p className="text-[9px] text-gray-400 uppercase tracking-wide font-medium flex items-center gap-1"><span>📅</span> Prazo</p>
                {editMode ? (
                  <input
                    type="datetime-local"
                    value={prazo}
                    onChange={(e) => setPrazo(e.target.value)}
                    className="w-full text-[11px] font-bold bg-white border border-gray-300 rounded px-1 py-0 focus:outline-none focus:ring-1 focus:ring-gray-400"
                  />
                ) : (
                  <p className={`text-xs font-bold ${isAtrasada ? 'text-red-600' : 'text-amber-700'}`}>
                    {formatDateTime(task.prazo)}
                  </p>
                )}
              </div>
              
              {/* Criada */}
              <div className="bg-gradient-to-br from-emerald-50 to-white rounded-xl px-3 py-2 border border-emerald-100 shadow-sm hover:shadow transition-shadow">
                <p className="text-[9px] text-gray-400 uppercase tracking-wide font-medium flex items-center gap-1"><span>🕐</span> Criada</p>
                <p className="text-xs font-semibold text-emerald-700">{formatDateTime(task.criadaEm)}</p>
              </div>
            </div>

            {/* Mencionados (observadores) - quem mais enxerga a tarefa */}
            <div className="flex flex-wrap items-center gap-1.5 mb-1.5">
              <span className="text-[9px] text-gray-400 uppercase tracking-wide font-medium">Mencionados</span>
              {observadoresIds.length === 0 && (
                <span className="text-[11px] text-gray-400 italic">ninguém</span>
              )}
              {observadoresIds.map(id => {
                const c = getColaboradorById(id);
                return (
                  <span key={id} className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-100 text-gray-700 rounded-full text-[11px] font-medium">
                    @{c?.nome || id}
                    {canMencionar && (
                      <button
                        onClick={() => salvarObservadores(observadoresIds.filter(o => o !== id))}
                        className="hover:text-red-600"
                        title="Remover menção"
                      >
                        <XMarkIcon className="h-3 w-3" />
                      </button>
                    )}
                  </span>
                );
              })}
              {canMencionar && (
                <select
                  value=""
                  onChange={(e) => {
                    const id = e.target.value;
                    if (id && !observadoresIds.includes(id)) salvarObservadores([...observadoresIds, id]);
                  }}
                  className="text-[11px] border border-gray-200 rounded-full px-2 py-0.5 text-gray-600 bg-white focus:outline-none focus:ring-1 focus:ring-gray-300"
                  title="Mencionar alguém (dá acesso à tarefa)"
                >
                  <option value="">+ mencionar</option>
                  {colaboradores
                    .filter(c => !observadoresIds.includes(c.id) && c.id !== task.responsavelId && c.id !== task.solicitanteId)
                    .map(c => (
                      <option key={c.id} value={c.id}>{c.nome}</option>
                    ))}
                </select>
              )}
            </div>
          </div>
        </div>

        {/* Alerta de Justificativa - para responsável, líder do responsável ou admin */}
        {isAtrasada && !temJustificativa && canJustify && (
          <div className="bg-red-50 border-b border-red-100 px-3 sm:px-6 py-2 flex items-center justify-between flex-shrink-0">
            <div className="flex items-center gap-2">
              <ExclamationTriangleIcon className="h-4 w-4 text-red-500" />
              <p className="text-xs font-medium text-red-800">
                Tarefa em atraso - Justificativa obrigatória
                {isLiderDoResponsavel && !isResponsavel && (
                  <span className="ml-1 text-red-600">(você é líder de {responsavel?.nome})</span>
                )}
              </p>
            </div>
            <button onClick={() => setShowJustificativaModal(true)} className="px-3 py-1 bg-red-600 text-white text-xs font-medium rounded-lg hover:bg-red-700">
              Justificar
            </button>
          </div>
        )}
        
        {/* Banner de Sucesso - Justificativa Enviada com detalhes */}
        {isAtrasada && temJustificativa && (
          <div
            className={`border-b px-3 sm:px-6 py-3 flex-shrink-0 ${
              isSolicitante || isLiderDoResponsavel
                ? 'bg-amber-50 border-amber-200'
                : 'bg-emerald-50 border-emerald-200'
            }`}
          >
            <div className="flex items-start gap-3">
              <CheckCircleIcon
                className={`h-5 w-5 mt-0.5 flex-shrink-0 ${isSolicitante || isLiderDoResponsavel ? 'text-amber-600' : 'text-emerald-600'}`}
              />
              <div className="flex-1 min-w-0">
                <p className={`text-xs font-bold mb-1 ${isSolicitante || isLiderDoResponsavel ? 'text-amber-800' : 'text-emerald-800'}`}>
                  {isSolicitante || isLiderDoResponsavel
                    ? '📋 Justificativa Recebida'
                    : '✅ Justificativa Enviada'}
                </p>
                <div className="bg-white/70 rounded-lg p-2 border border-gray-200/50">
                  <p className="text-xs text-gray-700 italic">"{justificativaAtrasoLocal}"</p>
                  {task.justificativaPor && (
                    <p className="text-[10px] text-gray-500 mt-1">
                      — Por: {colaboradores.find(c => c.id === task.justificativaPor || c.userId === task.justificativaPor)?.nome || 'Usuário'}
                      {task.justificativaEm && ` em ${formatDateTime(task.justificativaEm)}`}
                    </p>
                  )}
                </div>
                {canRedefinirPrazo && (
                  <p className="text-[10px] text-amber-700 mt-2 font-medium">
                    👆 Defina um novo prazo clicando em "Redefinir Prazo" abaixo.
                  </p>
                )}
              </div>
            </div>
          </div>
        )}
        
        {/* Aviso para outros usuários que não podem justificar */}
        {isAtrasada && !temJustificativa && !canJustify && (
          <div className="bg-amber-50 border-b border-amber-100 px-3 sm:px-6 py-2 flex items-center gap-2 flex-shrink-0">
            <ExclamationTriangleIcon className="h-4 w-4 text-amber-500" />
            <p className="text-xs font-medium text-amber-800">Tarefa em atraso - Aguardando justificativa do responsável</p>
          </div>
        )}

        {/* ========== CONTENT - DUAS COLUNAS ========== */}
        <div className="flex-1 overflow-hidden flex flex-col md:flex-row">
          {/* COLUNA ESQUERDA: Comentários + Anexos + Histórico */}
          <div className="w-full md:w-[50%] h-auto md:h-full overflow-y-auto border-b md:border-b-0 md:border-r border-gray-100 bg-gradient-to-b from-slate-50/80 to-white">
            <div className="sticky top-0 bg-white/95 backdrop-blur-sm border-b border-gray-100 px-5 py-3 z-10">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-slate-600 to-slate-800 flex items-center justify-center shadow-sm">
                  <ChatBubbleLeftIcon className="w-4 h-4 text-white" />
                </div>
                <div>
                  <h3 className="text-sm font-bold text-gray-800">Histórico & Interações</h3>
                  <p className="text-[10px] text-gray-400">{comentarios.length} comentários</p>
                </div>
              </div>
            </div>

            <div className="p-4 space-y-4">
              {/* Comentários */}
              <div className="bg-white rounded-xl border border-gray-100 overflow-hidden shadow-sm">
                <div className="px-4 py-2.5 bg-gradient-to-r from-gray-50 to-white border-b border-gray-100 flex items-center gap-2">
                  <span className="text-base">💬</span>
                  <p className="text-xs font-bold text-gray-700">Comentários</p>
                  <span className="ml-auto text-[10px] text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full font-medium">{comentarios.length}</span>
                </div>
                <div className="p-3">
                  <div className="space-y-2 max-h-60 overflow-y-auto mb-3">
                    {comentarios.length > 0 ? (
                      comentarios.map((com, _idx) => {
                        const autor = getColaboradorById(com.autorId || (com as any).autorEmail || '');
                        const autorNome =
                          autor?.nome ||
                          (com as any).autorNome ||
                          (com as any).autorEmail ||
                          'Desconhecido';
                        const podeEditar = canEditComentario(com);
                        const estaEditando = editandoComentarioId === com.id;
                        
                        // Mesma cor do Solicitante (slate) ou Responsável (indigo) - comparando por nome também
                        const solicitanteNome = solicitante?.nome?.toLowerCase() || '';
                        const responsavelNome = responsavel?.nome?.toLowerCase() || '';
                        const autorNomeLower = autorNome?.toLowerCase() || '';
                        
                        const isSolicitanteAutor = autorNomeLower === solicitanteNome || 
                                                   com.autorId === task.solicitanteId ||
                                                   autor?.id === solicitante?.id;
                        const isResponsavelAutor = autorNomeLower === responsavelNome || 
                                                   com.autorId === task.responsavelId ||
                                                   autor?.id === responsavel?.id;
                        const avatarColor = isSolicitanteAutor 
                          ? 'bg-gradient-to-br from-slate-500 to-slate-700'
                          : isResponsavelAutor 
                            ? 'bg-gradient-to-br from-indigo-500 to-indigo-700'
                            : 'bg-gradient-to-br from-gray-500 to-gray-700';
                        
                        return (
                          <div key={com.id} className="flex gap-2.5 bg-gradient-to-r from-gray-50 to-white rounded-xl p-3 group border border-gray-100">
                            <div className={`w-8 h-8 rounded-full ${avatarColor} flex items-center justify-center text-[11px] font-bold text-white flex-shrink-0 shadow-sm`}>
                              {autorNome?.charAt(0) || '?'}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-1.5 mb-0.5">
                                <span className="text-[11px] font-semibold text-gray-900">{autorNome}</span>
                                <span className="text-[9px] text-gray-400">{formatDateTime(com.criadoEm)}</span>
                                {(com as any).editadoEm && (
                                  <span className="text-[9px] text-gray-400 italic">(editado)</span>
                                )}
                              </div>
                              {estaEditando ? (
                                <div className="flex gap-1 mt-1">
                                  <input
                                    type="text"
                                    value={textoEditandoComentario}
                                    onChange={(e) => setTextoEditandoComentario(e.target.value)}
                                    className="flex-1 px-2 py-1 text-[11px] border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-gray-400"
                                    autoFocus
                                  />
                                  <button
                                    onClick={() => {
                                      handleEditComentario(com.id, textoEditandoComentario);
                                      setEditandoComentarioId(null);
                                    }}
                                    className="px-2 py-1 bg-gray-700 text-white text-[10px] rounded hover:bg-gray-800"
                                  >
                                    Salvar
                                  </button>
                                  <button
                                    onClick={() => setEditandoComentarioId(null)}
                                    className="px-2 py-1 bg-gray-200 text-gray-700 text-[10px] rounded hover:bg-gray-300"
                                  >
                                    Cancelar
                                  </button>
                                </div>
                              ) : (
                                <p className="text-[11px] text-gray-700">{com.texto}</p>
                              )}
                            </div>
                            {podeEditar && !estaEditando && (
                              <div className="flex items-start gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                <button
                                  onClick={() => {
                                    setEditandoComentarioId(com.id);
                                    setTextoEditandoComentario(com.texto);
                                  }}
                                  className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded"
                                  title="Editar"
                                >
                                  <PencilIcon className="h-3 w-3" />
                                </button>
                                <button
                                  onClick={() => handleDeleteComentario(com.id)}
                                  className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                                  title="Excluir"
                                >
                                  <TrashIcon className="h-3 w-3" />
                                </button>
                              </div>
                            )}
                          </div>
                        );
                      })
                    ) : (
                      <div className="text-center py-6 text-gray-400">
                        <ChatBubbleLeftIcon className="h-6 w-6 mx-auto mb-1 text-gray-300" />
                        <p className="text-xs">Nenhum comentário ainda</p>
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={novoComentario}
                      onChange={(e) => setNovoComentario(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleAddComentario()}
                      placeholder="Escreva um comentário..."
                      className="flex-1 px-3 py-2 text-xs bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-400/30"
                    />
                    <button
                      onClick={handleAddComentario}
                      disabled={!novoComentario.trim()}
                      className="px-3 py-2 bg-gray-700 text-white text-xs font-medium rounded-lg hover:bg-gray-800 disabled:opacity-50"
                    >
                      Enviar
                    </button>
                  </div>
                </div>
              </div>

              {/* Ação Pendente - mostra apenas se está atrasada E tem justificativa pendente de redefinição */}
              {isAtrasada && temJustificativa && canRedefinirPrazo && (
                <div className="bg-amber-50 border-2 border-amber-400 rounded-xl overflow-hidden shadow-sm">
                  <div className="px-3 py-2 bg-gradient-to-r from-amber-200 to-amber-100 border-b border-amber-300 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <ExclamationTriangleIcon className="h-4 w-4 text-amber-700" />
                      <p className="text-xs font-bold text-amber-900">⚠️ Ação Necessária</p>
                    </div>
                    <button
                      onClick={() => {
                        setNovoPrazo(new Date(task.prazo).toISOString().slice(0, 16));
                        setShowRedefinirPrazoModal(true);
                      }}
                      className="px-3 py-1.5 bg-amber-600 text-white text-xs font-bold rounded-lg hover:bg-amber-700 flex items-center gap-1 shadow"
                    >
                      <ClockIcon className="h-3.5 w-3.5" />
                      Redefinir Prazo
                    </button>
                  </div>
                  <div className="p-3">
                    <p className="text-xs text-amber-800">
                      O responsável justificou o atraso. Revise e defina um novo prazo para a tarefa.
                    </p>
                  </div>
                </div>
              )}

              {/* Anexos */}
              <div className="bg-white rounded-xl border border-gray-100 overflow-hidden shadow-sm">
                <div className="px-4 py-2.5 bg-gradient-to-r from-gray-50 to-white border-b border-gray-100 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-base">📎</span>
                    <p className="text-xs font-bold text-gray-700">Anexos</p>
                  </div>
                  <span className="text-[10px] text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full font-medium">{anexos.length}/10</span>
                </div>
                <div className="p-3">
                  {anexos.length > 0 && (
                    <div className="space-y-1.5 mb-2">
                      {anexos.map(anexo => (
                        <div key={anexo.id} className="flex items-center gap-2 bg-gray-50 rounded-lg p-2 group">
                          <div className={`p-1 rounded ${anexo.tipo.startsWith('image/') ? 'bg-blue-100' : anexo.tipo === 'application/pdf' ? 'bg-red-100' : 'bg-gray-200'}`}>
                            {getFileIcon(anexo.tipo)}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-[11px] font-medium text-gray-900 truncate">{anexo.nomeOriginal}</p>
                            <p className="text-[9px] text-gray-400">{formatFileSize(anexo.tamanho)}</p>
                          </div>
                          <a
                            href={`/api/tasks/${task.id}/anexos/${anexo.id}/view`}
                            target="_blank"
                            rel="noreferrer"
                            className="p-1 hover:bg-gray-200 rounded opacity-0 group-hover:opacity-100"
                            title="Abrir"
                          >
                            <EyeIcon className="h-3 w-3 text-gray-600" />
                          </a>
                          <a
                            href={`/api/tasks/${task.id}/anexos/${anexo.id}`}
                            className="p-1 hover:bg-gray-200 rounded opacity-0 group-hover:opacity-100"
                            title="Baixar"
                          >
                            <ArrowDownTrayIcon className="h-3 w-3 text-gray-600" />
                          </a>
                          <button onClick={() => handleRemoveAnexo(anexo.id)} className="p-1 hover:bg-red-100 rounded opacity-0 group-hover:opacity-100">
                            <TrashIcon className="h-3 w-3 text-red-500" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  <input ref={fileInputRef} type="file" multiple onChange={(e) => handleFileUpload(e.target.files)} className="hidden" />
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading || anexos.length >= 10}
                    className="w-full flex items-center justify-center gap-1.5 px-3 py-2 border-2 border-dashed border-gray-300 rounded-lg text-xs text-gray-500 hover:border-gray-400 disabled:opacity-50"
                  >
                    {uploading ? <><ArrowPathIcon className="h-3.5 w-3.5 animate-spin" /> Enviando...</> : <><PlusIcon className="h-3.5 w-3.5" /> Adicionar anexo</>}
                  </button>
                </div>
              </div>

              {/* Histórico */}
              {task.historico && task.historico.length > 0 && (
                <div className="bg-white rounded-xl border border-gray-100 overflow-hidden shadow-sm">
                  <button 
                    onClick={() => setLinhaTempoMinimizada(!linhaTempoMinimizada)}
                    className="w-full px-4 py-2.5 bg-gradient-to-r from-gray-50 to-white border-b border-gray-100 flex items-center gap-2 hover:bg-gray-50 transition-colors"
                  >
                    <span className="text-base">📜</span>
                    <p className="text-xs font-bold text-gray-700">Linha do tempo</p>
                    <span className="ml-auto text-[10px] text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full font-medium">{task.historico.length}</span>
                    <svg className={`w-4 h-4 text-gray-400 transition-transform ${linhaTempoMinimizada ? '' : 'rotate-180'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {!linhaTempoMinimizada && (
                    <div className="p-3 max-h-48 overflow-y-auto">
                    <div className="space-y-2">
                      {(() => {
                        // Criar histórico combinado com justificativa do responsável se existir
                        const historicoBase = task.historico.slice();
                        
                        // Verificar se a justificativa do responsável já está no histórico
                        const justificativaJaNoHistorico = historicoBase.some(h => 
                          (h.acao === 'justificativa' || (h as any).tipo === 'justificativa') ||
                          h.descricao?.includes('Justificativa de atraso')
                        );
                        
                        // Se tem justificativa mas não está no histórico, adicionar como item virtual
                        if (task.justificativaAtraso && !justificativaJaNoHistorico) {
                          const responsavelNome = colaboradores.find(c => 
                            c.id === task.justificativaPor || c.userId === task.justificativaPor
                          )?.nome || 'Responsável';
                          
                          historicoBase.push({
                            id: 'justificativa-virtual',
                            acao: 'justificativa' as const,
                            descricao: `Justificativa de atraso enviada: "${task.justificativaAtraso}"`,
                            realizadoPor: responsavelNome,
                            realizadoEm: task.justificativaEm || task.atualizadaEm || new Date()
                          });
                        }
                        
                        // Ordenar por data (mais recente primeiro)
                        return historicoBase
                          .sort((a, b) => new Date(b.realizadoEm || (b as any).data || 0).getTime() - new Date(a.realizadoEm || (a as any).data || 0).getTime())
                          .map((h, idx) => {
                        const hAny = h as any; // Para lidar com dados legados
                        const isPrazoAlterado = (h.acao ?? hAny.tipo) === 'prazo_alterado';
                        const isJustificativa = (h.acao ?? hAny.tipo) === 'justificativa' || h.descricao?.includes('Justificativa de atraso');
                        const autorId =
                          hAny.usuarioId ||
                          (typeof h.realizadoPor === 'string' ? h.realizadoPor : '') ||
                          '';
                        const realizadoPorStr = typeof h.realizadoPor === 'string' ? h.realizadoPor : '';
                        const pareceId =
                          /^[a-f0-9]{24}$/i.test(realizadoPorStr) ||
                          (realizadoPorStr.length >= 18 && !realizadoPorStr.includes(' '));
                        const autorNome =
                          getColaboradorById(autorId)?.nome ||
                          (!pareceId && realizadoPorStr ? realizadoPorStr : '') ||
                          'Desconhecido';
                        
                        // Estilo especial para justificativas
                        if (isJustificativa) {
                          return (
                            <div 
                              key={h.id || idx} 
                              className="bg-amber-50 border border-amber-300 rounded-lg p-2.5 -mx-1"
                            >
                              <div className="flex items-start gap-2">
                                <div className="w-6 h-6 rounded-full bg-amber-500 flex items-center justify-center flex-shrink-0">
                                  <ExclamationTriangleIcon className="w-3.5 h-3.5 text-white" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-[10px] font-bold text-amber-800 uppercase tracking-wide">💬 Justificativa de Atraso</p>
                                  <p className="text-[11px] text-amber-900 mt-1 bg-white/70 rounded px-2 py-1.5 border border-amber-200">
                                    {h.descricao?.replace('Justificativa de atraso enviada: ', '').replace(/^"|"$/g, '')}
                                  </p>
                                  <div className="flex items-center gap-2 mt-1.5">
                                    <div className="w-4 h-4 rounded-full bg-amber-300 flex items-center justify-center text-[8px] font-bold text-amber-800">
                                      {autorNome.charAt(0)}
                                    </div>
                                    <p className="text-[9px] text-amber-700 font-medium">{autorNome}</p>
                                    <span className="text-[8px] text-amber-400">•</span>
                                    <p className="text-[9px] text-amber-600">{formatDateTime(h.realizadoEm || hAny.data)}</p>
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        }
                        
                        // Estilo especial para prazo alterado
                        if (isPrazoAlterado) {
                          return (
                            <div 
                              key={h.id || idx} 
                              className="bg-blue-50 border border-blue-200 rounded-lg p-2.5 -mx-1"
                            >
                              <div className="flex items-start gap-2">
                                <div className="w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0">
                                  <ClockIcon className="w-3.5 h-3.5 text-white" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-[10px] font-bold text-blue-800 uppercase tracking-wide">📅 Prazo Redefinido</p>
                                  <p className="text-[11px] text-blue-900 mt-1">
                                    {h.descricao}
                                  </p>
                                  <div className="flex items-center gap-2 mt-1.5">
                                    <div className="w-4 h-4 rounded-full bg-blue-300 flex items-center justify-center text-[8px] font-bold text-blue-800">
                                      {autorNome.charAt(0)}
                                    </div>
                                    <p className="text-[9px] text-blue-700 font-medium">{autorNome}</p>
                                    <span className="text-[8px] text-blue-400">•</span>
                                    <p className="text-[9px] text-blue-600">{formatDateTime(h.realizadoEm || hAny.data)}</p>
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        }
                        
                        // Estilo padrão para outros eventos
                        return (
                          <div 
                            key={h.id || idx} 
                            className="flex items-start gap-2"
                          >
                            <div className="w-1.5 h-1.5 rounded-full bg-gray-400 mt-1.5 flex-shrink-0"></div>
                            <div className="flex-1 min-w-0">
                              <p className="text-[11px] text-gray-700">
                                {h.descricao}
                              </p>
                              <div className="flex items-center gap-2 mt-0.5">
                                <p className="text-[9px] text-gray-400">{autorNome}</p>
                                <span className="text-[8px] text-gray-300">•</span>
                                <p className="text-[9px] text-gray-400">{formatDateTime(h.realizadoEm || hAny.data)}</p>
                              </div>
                            </div>
                          </div>
                        );
                      });
                      })()}
                    </div>
                  </div>
                  )}
                </div>
              )}

              {/* Escalonamentos */}
              {task.escalonamentos && task.escalonamentos.length > 0 && (
                <div className="bg-amber-50 rounded-xl border border-amber-200 overflow-hidden">
                  <div className="px-3 py-2 bg-amber-100/50 border-b border-amber-200 flex items-center gap-2">
                    <span className="text-sm">🔔</span>
                    <p className="text-xs font-bold text-amber-700">Escalonamentos</p>
                  </div>
                  <div className="p-3 space-y-2">
                    {task.escalonamentos.map((esc, idx) => {
                      const escResponsavel = getColaboradorById(esc.responsavelId);
                      return (
                        <div key={idx} className="bg-white rounded-lg p-2 border border-amber-200">
                          <div className="flex items-center gap-2">
                            <span className="w-4 h-4 bg-amber-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center">{esc.nivel}</span>
                            <span className="text-xs font-medium text-amber-800">{escResponsavel?.nome || 'Desconhecido'}</span>
                          </div>
                          <p className="text-[10px] text-amber-600 mt-1">{esc.motivoAutomatico}</p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* COLUNA DIREITA: Campos Personalizados */}
          <div className="w-full md:w-[50%] h-auto md:h-full overflow-y-auto bg-gradient-to-b from-gray-50/80 to-white md:border-l border-gray-200">
            <div className="sticky top-0 bg-white/95 backdrop-blur-sm border-b border-gray-100 px-5 py-3 z-10">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-indigo-500 to-indigo-700 flex items-center justify-center shadow-sm">
                    <Squares2X2Icon className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-gray-800">Campos Personalizados</h3>
                    <p className="text-[10px] text-gray-400">{camposConfig?.length || 0} campos configurados</p>
                  </div>
                </div>
                {canEditCampos && (
                  <button
                    onClick={() => setShowConfigCampos(true)}
                    className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                    title="Configurar campos"
                  >
                    <Cog6ToothIcon className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>

            <div className="p-4 space-y-3">
              {camposConfig && camposConfig.length > 0 ? (
                camposConfig.map(campo => {
                  const valor = camposValores[campo.id];
                  const tipoConfig: Record<string, { icon: string; color: string }> = {
                    texto: { icon: '📝', color: 'bg-gray-100 text-gray-700' },
                    texto_longo: { icon: '📄', color: 'bg-gray-100 text-gray-700' },
                    data: { icon: '📅', color: 'bg-gray-100 text-gray-700' },
                    lista: { icon: '📋', color: 'bg-gray-100 text-gray-700' },
                    checkbox: { icon: '☑️', color: 'bg-gray-100 text-gray-700' },
                    checkbox_multi: { icon: '✅', color: 'bg-gray-100 text-gray-700' },
                    lista_progresso: { icon: '☰', color: 'bg-slate-100 text-slate-700' },
                    numero: { icon: '🔢', color: 'bg-gray-100 text-gray-700' },
                    valor: { icon: '💰', color: 'bg-gray-100 text-gray-700' },
                    telefone: { icon: '📞', color: 'bg-gray-100 text-gray-700' },
                    email: { icon: '📧', color: 'bg-gray-100 text-gray-700' },
                    link: { icon: '🔗', color: 'bg-gray-100 text-gray-700' }
                  };
                  const config = tipoConfig[campo.tipo] || { icon: '📝', color: 'bg-gray-100 text-gray-700' };
                  
                  return (
                    <div key={campo.id} className="bg-white rounded-xl border border-gray-100 overflow-hidden shadow-sm hover:shadow transition-shadow">
                      <div className="px-4 py-2.5 bg-gradient-to-r from-slate-50 to-white border-b border-gray-100 flex items-center gap-3">
                        <span className={`w-8 h-8 rounded-lg flex items-center justify-center text-sm shadow-sm ${config.color}`}>
                          {config.icon}
                        </span>
                        <div className="flex-1">
                          <p className="text-sm font-semibold text-gray-800">{campo.nome}</p>
                          {campo.obrigatorio && <span className="text-[9px] text-red-500 font-medium">Obrigatório</span>}
                        </div>
                      </div>
                      
                      <div className="p-4">
                        {campo.tipo === 'texto' && (
                          <input
                            type="text"
                            value={valor || ''}
                            onChange={(e) => handleCampoChange(campo.id, e.target.value)}
                            placeholder={campo.placeholder || `Digite...`}
                            className="w-full px-2.5 py-2 text-xs bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-400/30"
                          />
                        )}
                        
                        {campo.tipo === 'texto_longo' && (
                          <textarea
                            value={valor || ''}
                            onChange={(e) => handleCampoChange(campo.id, e.target.value)}
                            placeholder={campo.placeholder || `Digite...`}
                            rows={3}
                            className="w-full px-2.5 py-2 text-xs bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-400/30 resize-none"
                          />
                        )}
                        
                        {campo.tipo === 'data' && (
                          <input
                            type="datetime-local"
                            value={valor ? new Date(valor).toISOString().slice(0, 16) : ''}
                            onChange={(e) => handleCampoChange(campo.id, e.target.value ? new Date(e.target.value) : null)}
                            className="w-full px-2.5 py-2 text-xs bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-400/30"
                          />
                        )}
                        
                        {campo.tipo === 'lista' && (
                          <select
                            value={valor || ''}
                            onChange={(e) => handleCampoChange(campo.id, e.target.value)}
                            className="w-full px-2.5 py-2 text-xs bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-400/30"
                          >
                            <option value="">Selecione...</option>
                            {campo.opcoes?.map((opcao, idx) => (
                              <option key={idx} value={opcao}>{opcao}</option>
                            ))}
                          </select>
                        )}
                        
                        {campo.tipo === 'checkbox' && (
                          <label className="flex items-center gap-2 cursor-pointer p-1.5 rounded-lg hover:bg-gray-50">
                            <input
                              type="checkbox"
                              checked={!!valor}
                              onChange={(e) => handleCampoChange(campo.id, e.target.checked)}
                              className="w-4 h-4 rounded border-gray-300 text-gray-700 focus:ring-gray-400"
                            />
                            <span className="text-xs text-gray-700">{valor ? '✅ Sim' : '⬜ Não'}</span>
                          </label>
                        )}
                        
                        {campo.tipo === 'checkbox_multi' && (
                          <div className="space-y-1.5">
                            {campo.opcoes?.map((opcao, idx) => (
                              <label key={idx} className="flex items-center gap-2 cursor-pointer p-1.5 rounded-lg hover:bg-gray-50 border border-gray-100">
                                <input
                                  type="checkbox"
                                  checked={Array.isArray(valor) && valor.includes(opcao)}
                                  onChange={(e) => {
                                    const current = Array.isArray(valor) ? valor : [];
                                    if (e.target.checked) {
                                      handleCampoChange(campo.id, [...current, opcao]);
                                    } else {
                                      handleCampoChange(campo.id, current.filter((v: string) => v !== opcao));
                                    }
                                  }}
                                  className="w-3.5 h-3.5 rounded border-gray-300 text-gray-700 focus:ring-gray-400"
                                />
                                <span className="text-xs text-gray-700">{opcao}</span>
                              </label>
                            ))}
                          </div>
                        )}
                        
                        {campo.tipo === 'numero' && (
                          <input
                            type="number"
                            value={valor || ''}
                            onChange={(e) => handleCampoChange(campo.id, e.target.value ? Number(e.target.value) : null)}
                            placeholder="0"
                            className="w-full px-2.5 py-2 text-xs bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-400/30"
                          />
                        )}
                        
                        {campo.tipo === 'valor' && (
                          <div className="relative">
                            <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-xs text-gray-500 font-medium">R$</span>
                            <input
                              type="number"
                              step="0.01"
                              value={valor || ''}
                              onChange={(e) => handleCampoChange(campo.id, e.target.value ? Number(e.target.value) : null)}
                              placeholder="0,00"
                              className="w-full pl-9 pr-2.5 py-2 text-xs bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-400/30"
                            />
                          </div>
                        )}
                        
                        {campo.tipo === 'telefone' && (
                          <input
                            type="tel"
                            value={valor || ''}
                            onChange={(e) => handleCampoChange(campo.id, e.target.value)}
                            placeholder="(00) 00000-0000"
                            className="w-full px-2.5 py-2 text-xs bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-400/30"
                          />
                        )}
                        
                        {campo.tipo === 'email' && (
                          <input
                            type="email"
                            value={valor || ''}
                            onChange={(e) => handleCampoChange(campo.id, e.target.value)}
                            placeholder="exemplo@email.com"
                            className="w-full px-2.5 py-2 text-xs bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-400/30"
                          />
                        )}
                        
                        {campo.tipo === 'link' && (
                          <input
                            type="url"
                            value={valor || ''}
                            onChange={(e) => handleCampoChange(campo.id, e.target.value)}
                            placeholder="https://..."
                            className="w-full px-2.5 py-2 text-xs bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-400/30"
                          />
                        )}
                        
                        {/* Lista de Progresso */}
                        {campo.tipo === 'lista_progresso' && (
                          <div className="space-y-3">
                            {(() => {
                              const prog = calcularProgressoLista(campo.id);
                              return (
                                <div className="bg-gradient-to-r from-slate-50 to-gray-50 rounded-lg p-3 border border-slate-200">
                                  <div className="flex items-center justify-between mb-2">
                                    <div className="flex items-center gap-2">
                                      <div className="w-6 h-6 rounded flex items-center justify-center bg-slate-200">
                                        <span className="text-sm text-slate-600">{prog.porcentagem === 100 ? '✓' : '☰'}</span>
                                      </div>
                                      <div>
                                        <span className="text-xs font-bold text-slate-800">{prog.concluidos} de {prog.total}</span>
                                        <p className="text-[9px] text-slate-500">{prog.total === 0 ? 'Adicione itens' : prog.porcentagem === 100 ? 'Concluído' : 'Em progresso'}</p>
                                      </div>
                                    </div>
                                    <span className="text-base font-bold text-slate-700">{prog.porcentagem}%</span>
                                  </div>
                                  <div className="w-full h-1.5 bg-slate-200 rounded-full overflow-hidden">
                                    <div 
                                      className="h-full transition-all duration-500 rounded-full bg-slate-500"
                                      style={{ width: `${prog.porcentagem}%` }}
                                    />
                                  </div>
                                </div>
                              );
                            })()}

                            <div className="space-y-2 max-h-56 overflow-y-auto pr-1">
                              {((camposValores[campo.id] || []) as ItemProgresso[]).map((item, index) => (
                                <div 
                                  key={item.id}
                                  draggable={Boolean(canEditCampos)}
                                  onDragStart={(e) => {
                                    if (!canEditCampos) return;
                                    setDraggedItemIndex(index);
                                    setDraggedCampoId(campo.id);
                                    e.dataTransfer.effectAllowed = 'move';
                                  }}
                                  onDragOver={(e) => {
                                    if (!canEditCampos) return;
                                    e.preventDefault();
                                    e.dataTransfer.dropEffect = 'move';
                                  }}
                                  onDrop={(e) => {
                                    if (!canEditCampos || draggedItemIndex === null || draggedCampoId !== campo.id) return;
                                    e.preventDefault();
                                    reordenarItemProgresso(campo.id, draggedItemIndex, index);
                                    setDraggedItemIndex(null);
                                    setDraggedCampoId(null);
                                  }}
                                  onDragEnd={() => {
                                    setDraggedItemIndex(null);
                                    setDraggedCampoId(null);
                                  }}
                                  className={`flex items-center gap-2 px-3 py-2.5 rounded-xl border transition-all group ${
                                    item.concluido 
                                      ? 'bg-gradient-to-r from-emerald-50 to-white border-emerald-200' 
                                      : 'bg-white border-gray-100 hover:border-indigo-200 hover:shadow-sm'
                                  } ${canEditCampos ? 'cursor-move' : ''} ${draggedItemIndex === index && draggedCampoId === campo.id ? 'opacity-50 scale-[0.98]' : ''}`}
                                >
                                  {canEditCampos && (
                                    <div className="flex items-center text-gray-300 cursor-move hover:text-gray-500 transition-colors">
                                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                        <path d="M7 2a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 2zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 8zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 14zm6-8a2 2 0 1 0-.001-4.001A2 2 0 0 0 13 6zm0 2a2 2 0 1 0 .001 4.001A2 2 0 0 0 13 8zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 13 14z"></path>
                                      </svg>
                                    </div>
                                  )}
                                  
                                  <span className={`w-5 h-5 rounded-lg flex items-center justify-center text-[10px] font-bold flex-shrink-0 ${
                                    item.concluido ? 'bg-emerald-500 text-white' : 'bg-slate-100 text-slate-600'
                                  }`}>{index + 1}</span>
                                  
                                  <button
                                    onClick={() => toggleItemProgresso(campo.id, item.id)}
                                    className={`w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 transition-all ${
                                      item.concluido 
                                        ? 'bg-emerald-500 border-emerald-500 text-white' 
                                        : 'border-gray-300 hover:border-indigo-400 hover:bg-indigo-50'
                                    }`}
                                  >
                                    {item.concluido && <CheckCircleIcon className="h-3 w-3" />}
                                  </button>
                                  
                                  <div className="flex-1 min-w-0">
                                    <p className={`text-sm ${item.concluido ? 'text-gray-400 line-through' : 'text-gray-800 font-medium'}`}>{item.nome}</p>
                                    {item.concluido && item.concluidoEm && (
                                      <p className="text-[10px] text-emerald-600 font-medium">✓ {new Date(item.concluidoEm).toLocaleDateString('pt-BR')}</p>
                                    )}
                                    {item.comentario && comentarioItem?.itemId !== item.id && (
                                      <button 
                                        onClick={() => setComentarioItem({ campoId: campo.id, itemId: item.id, texto: item.comentario || '' })}
                                        className="text-[10px] text-amber-700 mt-1 bg-amber-50 px-2 py-1 rounded-lg inline-flex items-center gap-1 hover:bg-amber-100 transition-colors cursor-pointer"
                                        title="Clique para editar"
                                      >
                                        💬 {item.comentario}
                                        <PencilIcon className="h-2.5 w-2.5 text-amber-500" />
                                      </button>
                                    )}
                                    {comentarioItem?.campoId === campo.id && comentarioItem?.itemId === item.id && (
                                      <div className="mt-1.5 flex gap-1">
                                        <input
                                          type="text"
                                          value={comentarioItem.texto}
                                          onChange={(e) => setComentarioItem({ ...comentarioItem, texto: e.target.value })}
                                          placeholder="Observação..."
                                          className="flex-1 px-2 py-1 text-[10px] border rounded focus:outline-none focus:ring-1 focus:ring-gray-400"
                                          autoFocus
                                        />
                                        <button onClick={() => adicionarComentarioItem(campo.id, item.id, comentarioItem.texto)} className="px-1.5 py-1 bg-gray-700 text-white text-[9px] rounded">OK</button>
                                        <button onClick={() => setComentarioItem(null)} className="px-1.5 py-1 bg-gray-200 text-[9px] rounded">✕</button>
                                      </div>
                                    )}
                                  </div>
                                  
                                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                    {!comentarioItem && (
                                      <button onClick={() => setComentarioItem({ campoId: campo.id, itemId: item.id, texto: item.comentario || '' })} className="p-1 text-gray-400 hover:text-amber-500 hover:bg-amber-50 rounded-lg transition-colors">
                                        <ChatBubbleLeftIcon className="h-4 w-4" />
                                      </button>
                                    )}
                                    <button onClick={() => removerItemProgresso(campo.id, item.id)} className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors">
                                      <TrashIcon className="h-4 w-4" />
                                    </button>
                                  </div>
                                </div>
                              ))}
                            </div>

                            <div className="flex gap-2 bg-gradient-to-r from-slate-50 to-white p-3 rounded-xl border border-dashed border-slate-200">
                              <input
                                type="text"
                                value={novoItemProgresso[campo.id] || ''}
                                onChange={(e) => setNovoItemProgresso(prev => ({ ...prev, [campo.id]: e.target.value }))}
                                onKeyPress={(e) => e.key === 'Enter' && adicionarItemProgresso(campo.id, novoItemProgresso[campo.id] || '')}
                                placeholder="Novo item..."
                                className="flex-1 px-3 py-2 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-400/30 focus:border-indigo-300"
                              />
                              <button
                                onClick={() => adicionarItemProgresso(campo.id, novoItemProgresso[campo.id] || '')}
                                disabled={!(novoItemProgresso[campo.id] || '').trim()}
                                className="px-4 py-2 bg-gradient-to-r from-indigo-500 to-indigo-600 text-white text-sm font-medium rounded-lg hover:from-indigo-600 hover:to-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5 shadow-sm transition-all"
                              >
                                <PlusIcon className="h-4 w-4" /> Add
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="flex flex-col items-center justify-center py-12 text-center bg-white rounded-lg border border-dashed border-gray-300">
                  <div className="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center mb-3">
                    <Squares2X2Icon className="w-7 h-7 text-gray-400" />
                  </div>
                  <p className="text-sm font-semibold text-gray-700 mb-1">Nenhum campo</p>
                  <p className="text-xs text-gray-500 mb-3">Configure campos para esta tarefa</p>
                  {canEditCampos && (
                    <button
                      onClick={() => setShowConfigCampos(true)}
                      className="px-3 py-1.5 bg-gray-700 text-white text-xs font-medium rounded-lg hover:bg-gray-800 flex items-center gap-1"
                    >
                      <Cog6ToothIcon className="w-3.5 h-3.5" />
                      Configurar Campos
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        {editMode && (
          <div className="px-3 sm:px-6 py-3 sm:py-4 border-t border-gray-200 bg-gray-50 flex items-center justify-end gap-3 flex-shrink-0">
            <button
              onClick={() => {
                setEditMode(false);
                setTitulo(task.titulo);
                setDescricao(task.descricao || '');
                setPrazo(new Date(task.prazo).toISOString().slice(0, 16));
                setResponsavelId(task.responsavelId);
                setPrioridade(task.prioridade);
              }}
              className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors"
            >
              Cancelar
            </button>
            <button
              onClick={handleSave}
              className="px-4 py-2 bg-gray-900 text-white text-sm font-medium rounded-lg hover:bg-gray-800 transition-colors"
            >
              Salvar
            </button>
          </div>
        )}

        {/* Delete Confirmation */}
        {showDeleteConfirm && (
          <div className="absolute inset-0 bg-white/95 flex items-center justify-center p-8 z-10">
            <div className="text-center max-w-sm">
              <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <TrashIcon className="h-7 w-7 text-red-600" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Excluir tarefa?</h3>
              <p className="text-sm text-gray-500 mb-6">Esta ação não pode ser desfeita.</p>
              <div className="flex items-center justify-center gap-3">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleDelete}
                  className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700"
                >
                  Excluir
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal de Justificativa */}
        {showJustificativaModal && (
          <div className="absolute inset-0 bg-black/40 flex items-center justify-center p-8 z-10">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-2">Justificar Atraso</h3>
              <p className="text-sm text-gray-500 mb-4">Informe o motivo do atraso. O líder será notificado.</p>
              <textarea
                value={justificativa}
                onChange={(e) => setJustificativa(e.target.value)}
                rows={4}
                className="w-full px-4 py-3 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-300 resize-none mb-4"
                placeholder="Descreva o motivo do atraso..."
              />
              <div className="flex items-center justify-end gap-3">
                <button
                  onClick={() => setShowJustificativaModal(false)}
                  className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
                >
                  Cancelar
                </button>
                <button
                  onClick={() => {
                    if (!justificativa.trim()) return;
                    const agora = new Date();
                    const novoHistorico = [
                      ...(task.historico || []),
                      {
                        id: `hist-${Date.now()}`,
                        acao: 'justificativa' as const,
                        descricao: `Justificativa de atraso enviada: "${justificativa.trim()}"`,
                        realizadoPor: currentColaborador?.nome || session.usuario.nome,
                        realizadoEm: agora
                      }
                    ];
                    const taskAtualizada = {
                      ...task,
                      justificativaAtraso: justificativa.trim(),
                      justificativaEm: agora,
                      justificativaPor: currentColaborador?.id || session.usuario.id,
                      historico: novoHistorico,
                      atualizadaEm: agora
                    };
                    // Atualizar estado local imediatamente
                    setJustificativaAtrasoLocal(justificativa.trim());
                    onUpdate(taskAtualizada);
                    setShowJustificativaModal(false);
                  }}
                  disabled={!justificativa.trim()}
                  className="px-4 py-2 bg-amber-500 text-white text-sm font-medium rounded-lg hover:bg-amber-600 disabled:opacity-50"
                >
                  Enviar
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal de Redefinir Prazo */}
        {showRedefinirPrazoModal && (
          <div className="absolute inset-0 bg-black/40 flex items-center justify-center p-8 z-10">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-2">Redefinir Prazo</h3>
              <p className="text-sm text-gray-500 mb-4">
                O responsável justificou o atraso. Defina um novo prazo para a tarefa.
              </p>
              
              {/* Prazo Atual */}
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
                <p className="text-xs font-semibold text-red-700 mb-1">Prazo atual (vencido):</p>
                <p className="text-sm text-red-800">{formatDateTime(task.prazo)}</p>
              </div>
              
              {/* Novo Prazo */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Novo Prazo *</label>
                <input
                  type="datetime-local"
                  value={novoPrazo}
                  onChange={(e) => setNovoPrazo(e.target.value)}
                  min={new Date().toISOString().slice(0, 16)}
                  className="w-full px-4 py-3 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-300"
                />
              </div>
              
              {/* Motivo da Prorrogação */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Motivo da Prorrogação (opcional)</label>
                <textarea
                  value={motivoProrrogacao}
                  onChange={(e) => setMotivoProrrogacao(e.target.value)}
                  rows={3}
                  className="w-full px-4 py-3 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-300 resize-none"
                  placeholder="Ex: Aprovado adiamento devido à complexidade da tarefa..."
                />
              </div>
              
              <div className="flex items-center justify-end gap-3">
                <button
                  onClick={() => {
                    setShowRedefinirPrazoModal(false);
                    setMotivoProrrogacao('');
                  }}
                  className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
                >
                  Cancelar
                </button>
                <button
                  onClick={() => {
                    if (!novoPrazo) return;
                    const prazoAntigo = new Date(task.prazo);
                    const prazoNovo = new Date(novoPrazo);
                    const agora = new Date();
                    
                    const novoHistorico = [
                      ...(task.historico || []),
                      {
                        id: `hist-${Date.now()}`,
                        acao: 'prazo_alterado' as const,
                        descricao: `Prazo prorrogado de ${formatDateTime(prazoAntigo)} para ${formatDateTime(prazoNovo)}${motivoProrrogacao ? `. Motivo: ${motivoProrrogacao}` : ''}`,
                        realizadoPor: currentColaborador?.nome || session.usuario.nome,
                        realizadoEm: agora,
                        dadosAnteriores: {
                          prazo: prazoAntigo.toISOString()
                        },
                        dadosNovos: {
                          prazo: prazoNovo.toISOString(),
                          motivo: motivoProrrogacao || undefined
                        }
                      }
                    ];
                    
                    const taskAtualizada = {
                      ...task,
                      prazo: prazoNovo,
                      justificativaAtraso: undefined, // Limpa justificativa após redefinir prazo
                      justificativaEm: undefined,
                      justificativaPor: undefined,
                      historico: novoHistorico,
                      atualizadaEm: agora
                    };
                    onUpdate(taskAtualizada);
                    
                    setJustificativaAtrasoLocal(''); // Limpa localmente
                    setShowRedefinirPrazoModal(false);
                    setMotivoProrrogacao('');
                    setNovoPrazo(''); // Limpa o campo de novo prazo
                  }}
                  disabled={!novoPrazo}
                  className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  Confirmar Novo Prazo
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal de Configuração de Campos */}
        {showConfigCampos && (
          <ConfigCamposModal
            campos={camposConfig}
            onSave={(novosCampos) => {
              setCamposConfig(novosCampos);
              onUpdate({
                ...task,
                camposConfig: novosCampos,
                atualizadaEm: new Date()
              });
              setShowConfigCampos(false);
            }}
            onClose={() => setShowConfigCampos(false)}
          />
        )}
      </div>
    </div>
  );
};

// Modal de Configuração de Campos
interface ConfigCamposModalProps {
  campos: TaskCampoConfig[];
  onSave: (campos: TaskCampoConfig[]) => void;
  onClose: () => void;
}

const ConfigCamposModal = ({ campos, onSave, onClose }: ConfigCamposModalProps) => {
  const [camposEdit, setCamposEdit] = useState<TaskCampoConfig[]>(campos);
  const [novoCampo, setNovoCampo] = useState<Partial<TaskCampoConfig>>({
    nome: '',
    tipo: 'texto',
    obrigatorio: false,
    opcoes: []
  });
  const [novaOpcao, setNovaOpcao] = useState('');
  const [novaOpcaoEdit, setNovaOpcaoEdit] = useState<Record<string, string>>({});

  const tiposCampo = [
    { value: 'texto', label: 'Texto Curto', icon: '📝' },
    { value: 'texto_longo', label: 'Texto Longo', icon: '📄' },
    { value: 'data', label: 'Data/Hora', icon: '📅' },
    { value: 'lista', label: 'Lista (Select)', icon: '📋' },
    { value: 'checkbox', label: 'Checkbox', icon: '☑️' },
    { value: 'checkbox_multi', label: 'Múltipla Escolha', icon: '✅' },
    { value: 'lista_progresso', label: 'Lista de Progresso', icon: '☰' },
    { value: 'numero', label: 'Número', icon: '🔢' },
    { value: 'valor', label: 'Valor (R$)', icon: '💰' },
    { value: 'telefone', label: 'Telefone', icon: '📞' },
    { value: 'email', label: 'E-mail', icon: '📧' },
    { value: 'link', label: 'Link/URL', icon: '🔗' }
  ];

  const adicionarCampo = () => {
    if (!novoCampo.nome?.trim()) return;
    
    const campo: TaskCampoConfig = {
      id: `campo_${Date.now()}`,
      nome: novoCampo.nome.trim(),
      tipo: (novoCampo.tipo || 'texto') as any,
      obrigatorio: novoCampo.obrigatorio || false,
      placeholder: novoCampo.placeholder,
      opcoes: novoCampo.opcoes || []
    };
    
    setCamposEdit([...camposEdit, campo]);
    setNovoCampo({ nome: '', tipo: 'texto', obrigatorio: false, opcoes: [] });
    setNovaOpcao('');
  };

  const removerCampo = (campoId: string) => {
    setCamposEdit(camposEdit.filter(c => c.id !== campoId));
  };

  const atualizarCampo = (campoId: string, updates: Partial<TaskCampoConfig>) => {
    setCamposEdit(prev =>
      prev.map(c => (c.id === campoId ? { ...c, ...updates } : c))
    );
  };

  const adicionarOpcaoCampo = (campoId: string) => {
    const opcao = (novaOpcaoEdit[campoId] || '').trim();
    if (!opcao) return;
    setCamposEdit(prev =>
      prev.map(c =>
        c.id === campoId
          ? { ...c, opcoes: [...(c.opcoes || []), opcao] }
          : c
      )
    );
    setNovaOpcaoEdit(prev => ({ ...prev, [campoId]: '' }));
  };

  const removerOpcaoCampo = (campoId: string, opcao: string) => {
    setCamposEdit(prev =>
      prev.map(c =>
        c.id === campoId
          ? { ...c, opcoes: (c.opcoes || []).filter(o => o !== opcao) }
          : c
      )
    );
  };

  const adicionarOpcao = () => {
    if (!novaOpcao.trim()) return;
    setNovoCampo(prev => ({
      ...prev,
      opcoes: [...(prev.opcoes || []), novaOpcao.trim()]
    }));
    setNovaOpcao('');
  };

  const removerOpcao = (opcao: string) => {
    setNovoCampo(prev => ({
      ...prev,
      opcoes: (prev.opcoes || []).filter(o => o !== opcao)
    }));
  };

  const precisaOpcoes = ['lista', 'checkbox_multi'].includes(novoCampo.tipo || '');

  return (
    <div className="absolute inset-0 bg-black/50 flex items-center justify-center p-4 z-20">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-800">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center">
                <Cog6ToothIcon className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white">Configurar Campos</h3>
                <p className="text-xs text-gray-400">{camposEdit.length} campos configurados</p>
              </div>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-lg text-gray-400 hover:text-white">
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Campos existentes */}
          {camposEdit.length > 0 && (
            <div className="mb-6">
              <h4 className="text-sm font-semibold text-gray-700 mb-3">Campos Atuais</h4>
              <div className="space-y-2">
                {camposEdit.map((campo) => {
                  const tipo = tiposCampo.find(t => t.value === campo.tipo);
                  const precisaOpcoesCampo = ['lista', 'checkbox_multi'].includes(campo.tipo);
                  return (
                    <div key={campo.id} className="p-3 bg-gray-50 rounded-lg border border-gray-200 space-y-3">
                      <div className="flex items-center gap-3">
                        <span className="text-lg">{tipo?.icon || '📝'}</span>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-gray-500">Campo</p>
                          <input
                            type="text"
                            value={campo.nome}
                            onChange={(e) => atualizarCampo(campo.id, { nome: e.target.value })}
                            className="w-full px-2.5 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-400/30"
                          />
                        </div>
                        <button
                          onClick={() => removerCampo(campo.id)}
                          className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg"
                          title="Remover campo"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      </div>

                      <div className="grid grid-cols-3 gap-3">
                        <div>
                          <label className="block text-[11px] font-medium text-gray-600 mb-1">Tipo</label>
                          <select
                            value={campo.tipo}
                            onChange={(e) => atualizarCampo(campo.id, { tipo: e.target.value as any, opcoes: [] })}
                            className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-400/30"
                          >
                            {tiposCampo.map(tipo => (
                              <option key={tipo.value} value={tipo.value}>{tipo.icon} {tipo.label}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-[11px] font-medium text-gray-600 mb-1">Placeholder</label>
                          <input
                            type="text"
                            value={campo.placeholder || ''}
                            onChange={(e) => atualizarCampo(campo.id, { placeholder: e.target.value })}
                            className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-400/30"
                          />
                        </div>
                        <div className="flex items-end">
                          <label className="flex items-center gap-2 cursor-pointer text-xs text-gray-700">
                            <input
                              type="checkbox"
                              checked={campo.obrigatorio || false}
                              onChange={(e) => atualizarCampo(campo.id, { obrigatorio: e.target.checked })}
                              className="w-4 h-4 rounded border-gray-300 text-gray-700 focus:ring-gray-400"
                            />
                            Obrigatório
                          </label>
                        </div>
                      </div>

                      {precisaOpcoesCampo && (
                        <div>
                          <label className="block text-[11px] font-medium text-gray-600 mb-1">Opções</label>
                          <div className="flex gap-2 mb-2">
                            <input
                              type="text"
                              value={novaOpcaoEdit[campo.id] || ''}
                              onChange={(e) => setNovaOpcaoEdit(prev => ({ ...prev, [campo.id]: e.target.value }))}
                              onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), adicionarOpcaoCampo(campo.id))}
                              placeholder="Nova opção..."
                              className="flex-1 px-2.5 py-1.5 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-400/30"
                            />
                            <button
                              onClick={() => adicionarOpcaoCampo(campo.id)}
                              disabled={!(novaOpcaoEdit[campo.id] || '').trim()}
                              className="px-3 py-1.5 bg-gray-700 text-white text-xs font-medium rounded-lg hover:bg-gray-800 disabled:opacity-50"
                            >
                              Add
                            </button>
                          </div>
                          {(campo.opcoes || []).length > 0 && (
                            <div className="flex flex-wrap gap-1.5">
                              {(campo.opcoes || []).map((opcao, idx) => (
                                <span key={idx} className="inline-flex items-center gap-1 px-2 py-1 bg-white border border-gray-200 rounded text-xs">
                                  {opcao}
                                  <button onClick={() => removerOpcaoCampo(campo.id, opcao)} className="text-gray-400 hover:text-red-500">×</button>
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Adicionar novo campo */}
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
            <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
              <PlusIcon className="w-4 h-4" />
              Adicionar Novo Campo
            </h4>
            
            <div className="space-y-3">
              {/* Nome do campo */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Nome do Campo *</label>
                <input
                  type="text"
                  value={novoCampo.nome || ''}
                  onChange={(e) => setNovoCampo({ ...novoCampo, nome: e.target.value })}
                  placeholder="Ex: Observações, Data Limite, etc"
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-400/30"
                />
              </div>

              {/* Tipo do campo */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Tipo do Campo</label>
                <select
                  value={novoCampo.tipo || 'texto'}
                  onChange={(e) => setNovoCampo({ ...novoCampo, tipo: e.target.value as any, opcoes: [] })}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-400/30"
                >
                  {tiposCampo.map(tipo => (
                    <option key={tipo.value} value={tipo.value}>{tipo.icon} {tipo.label}</option>
                  ))}
                </select>
              </div>

              {/* Opções para lista/checkbox_multi */}
              {precisaOpcoes && (
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Opções</label>
                  <div className="flex gap-2 mb-2">
                    <input
                      type="text"
                      value={novaOpcao}
                      onChange={(e) => setNovaOpcao(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), adicionarOpcao())}
                      placeholder="Nova opção..."
                      className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-400/30"
                    />
                    <button
                      onClick={adicionarOpcao}
                      disabled={!novaOpcao.trim()}
                      className="px-3 py-2 bg-gray-700 text-white text-sm font-medium rounded-lg hover:bg-gray-800 disabled:opacity-50"
                    >
                      Add
                    </button>
                  </div>
                  {(novoCampo.opcoes || []).length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                      {(novoCampo.opcoes || []).map((opcao, idx) => (
                        <span key={idx} className="inline-flex items-center gap-1 px-2 py-1 bg-white border border-gray-200 rounded text-xs">
                          {opcao}
                          <button onClick={() => removerOpcao(opcao)} className="text-gray-400 hover:text-red-500">×</button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Placeholder */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Placeholder (opcional)</label>
                <input
                  type="text"
                  value={novoCampo.placeholder || ''}
                  onChange={(e) => setNovoCampo({ ...novoCampo, placeholder: e.target.value })}
                  placeholder="Texto de ajuda..."
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-400/30"
                />
              </div>

              {/* Obrigatório */}
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={novoCampo.obrigatorio || false}
                  onChange={(e) => setNovoCampo({ ...novoCampo, obrigatorio: e.target.checked })}
                  className="w-4 h-4 rounded border-gray-300 text-gray-700 focus:ring-gray-400"
                />
                <span className="text-sm text-gray-700">Campo obrigatório</span>
              </label>

              {/* Botão adicionar */}
              <button
                onClick={adicionarCampo}
                disabled={!novoCampo.nome?.trim() || (precisaOpcoes && (novoCampo.opcoes || []).length === 0)}
                className="w-full py-2 bg-gray-700 text-white text-sm font-medium rounded-lg hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                <PlusIcon className="w-4 h-4" />
                Adicionar Campo
              </button>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex items-center justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
          >
            Cancelar
          </button>
          <button
            onClick={() => onSave(camposEdit)}
            className="px-4 py-2 bg-gray-800 text-white text-sm font-medium rounded-lg hover:bg-gray-900"
          >
            Salvar Configuração
          </button>
        </div>
      </div>
    </div>
  );
};

export default Tasks;
